import {
  isTargetAttackNativeFrame,
  parseNativeGameFrame,
  type NativeGameFrame,
} from 'shared/lib/tv-game/nativeFrame.ts';
import { getGameOrNull } from 'shared/lib/tv-game/games.ts';
import type {
  ConnectionStatus,
  GameMode,
  IcePairType,
  NetworkCheckResult,
  LanCheckResult,
} from 'shared/lib/tv-game/types.ts';

import { RemoteFruitDisplayEngine } from './application/RemoteFruitDisplayEngine.ts';
import { TargetAttackDisplayEngine } from './application/TargetAttackDisplayEngine.ts';
import { CanvasRenderer } from './infrastructure/CanvasRenderer.ts';
import { detectTvQualityProfile, getTvQualityConfig } from './infrastructure/qualityProfile.ts';
import { SoundManager } from './infrastructure/SoundManager.ts';
import { TargetAttackSoundEngine } from './infrastructure/TargetAttackSoundEngine.ts';
import { TvSocketAdapter, PoseConsumerBridge } from './infrastructure/SocketAdapter.ts';
import { WebRTCManager, type IcePairInfo } from './infrastructure/WebRTCReceiver.ts';
import { keepScreenAwake } from './infrastructure/ScreenWakeLock.ts';
import { translate } from 'shared/lib/i18n.tsx';
import { buildLanQueryFromTvPage } from './lanFromLocation.ts';
import { TvRoomApi, TvRoomGoneError } from './infrastructure/tvRoomApi.ts';
import { computeTvCanvasBufferSize } from './tvCanvasBuffer.ts';
import {
  hideFruitRemoteHudAndPause,
  patchFruitNinjaRemoteUi,
  resetFruitNinjaRemoteUi,
  setFruitNinjaRemoteUiSnapshot,
} from './games/fruit-ninja-remote/state/fruitNinjaRemoteUiStore.ts';
import {
  patchTargetAttackUi,
  resetTargetAttackUi,
  setTargetAttackUiSnapshot,
} from './games/target-attack/state/targetAttackUiStore.ts';
import { IqDisplayEngine } from './application/IqDisplayEngine.ts';
import { patchIqUi, resetIqUi, setIqUiSnapshot } from './games/iq/state/iqUiStore.ts';
import { LegKickDisplayEngine } from './application/LegKickDisplayEngine.ts';
import { LegKickSoundEngine } from './infrastructure/LegKickSoundEngine.ts';
import {
  resetLegKickUi,
  setLegKickUiSnapshot,
} from './games/leg-kick/state/legKickUiStore.ts';
import { RunnerDisplayEngine } from './application/RunnerDisplayEngine.ts';
import { BirdFlightDisplayEngine } from './application/BirdFlightDisplayEngine.ts';
import { FruitSliceWebDisplayEngine } from './application/FruitSliceWebDisplayEngine.ts';
import { ChefMasterDisplayEngine } from './application/ChefMasterDisplayEngine.ts';
import { SkyRaceDisplayEngine } from './application/SkyRaceDisplayEngine.ts';
import { IqWebDisplayEngine } from './application/IqWebDisplayEngine.ts';
import { BodyPoseStore } from './application/BodyPoseStore.ts';
import { isBodyPoseFrame, parseBodyPoseFrame } from './application/bodyPoseFrame.ts';
import { HeadlessMenuOverlay } from './application/HeadlessMenuOverlay.ts';
import { HeadlessSkeletonOverlay } from './application/HeadlessSkeletonOverlay.ts';
import { HeadlessSkyRaceController } from './application/HeadlessSkyRaceController.ts';
import { LaserDodgeDisplayEngine } from './application/LaserDodgeDisplayEngine.ts';
import { patchRunnerUi, resetRunnerUi } from './games/runner/state/runnerUiStore.ts';
import { setCalibrationAlert } from './games/shared/state/calibrationAlertStore.ts';

export interface TvAppOptions {
  onLocaleChange?: (locale: 'kk' | 'ru' | 'en') => void;
}

export function startTvApp(opts: TvAppOptions = {}): () => void {
  function summarizeOutgoingIceCandidate(candidate: RTCIceCandidateInit): {
    candidateType: string | null;
    protocol: string | null;
    sdpMid: string | null;
  } {
    const raw = candidate.candidate;
    const candidateTypeMatch = typeof raw === 'string' ? raw.match(/\styp\s([a-zA-Z0-9_-]+)/) : null;
    const protocolMatch = typeof raw === 'string' ? raw.match(/(?:^|\s)(?:a=)?candidate:\S+\s+\d+\s+([a-zA-Z]+)/) : null;
    return {
      candidateType: candidateTypeMatch?.[1] ?? null,
      protocol: protocolMatch?.[1]?.toLowerCase() ?? null,
      sdpMid: candidate.sdpMid ?? null,
    };
  }

  const releaseWakeLock = keepScreenAwake();

  // ── DOM elements ─────────────────────────────────────────────────────────────
  const canvas = document.getElementById('canvas') as HTMLCanvasElement;
  const lobbyEl = document.getElementById('lobby') as HTMLDivElement;
  const tvLogoEl = document.getElementById('tv-logo') as HTMLElement;
  const countdownLayer = document.getElementById('countdown') as HTMLDivElement;
  const countdownGifEl = document.getElementById('countdown-gif') as HTMLImageElement;
  const lobbyHintChooseEl = document.getElementById('lobby-hint-choose') as HTMLDivElement;
  const lobbyHintReadyEl = document.getElementById('lobby-hint-ready') as HTMLDivElement;
  const lobbyHintReadyTitleEl = document.getElementById('lobby-hint-ready-title') as HTMLDivElement;
  const lobbyHintReadyTextEl = document.getElementById('lobby-hint-ready-text') as HTMLDivElement;

  // Numeric cache for syncFruitRemoteUi — avoids string alloc each frame.
  let _sigScore = -1; let _sigStreak = -1; let _sigLives = -1; let _sigSecs = -1;
  let _sigBestScore = -1; let _sigP2Score = -1; let _sigP2Lives = -1; let _sigP2Streak = -1;
  let _sigFlags = -1; // encodes isGameOver | isPaused | split packed as bits

  function syncFruitRemoteUi(): void {
    const s = remoteEngine.getState();
    const phase = s.phase;

    if (phase !== 'playing' && phase !== 'calibration') {
      hideFruitRemoteHudAndPause();
      return;
    }

    if (phase === 'calibration') {
      resetFruitNinjaRemoteUi();
      return;
    }

    const secs = s.timeLeft !== null ? Math.ceil(s.timeLeft) : -1;
    const hasCombo = s.combo !== null;
    const split = roomSelectedPlayerCount === 2;
    const flags = (s.isGameOver ? 1 : 0) | (s.isPaused ? 2 : 0) | (split ? 4 : 0) | (s.player1GameOver ? 8 : 0) | (s.player2GameOver ? 16 : 0);
    if (
      !hasCombo &&
      _sigScore === s.score &&
      _sigStreak === (s.streak ?? -1) &&
      _sigLives === (s.lives ?? -1) &&
      _sigSecs === secs &&
      _sigBestScore === (s.bestScore ?? -1) &&
      _sigP2Score === (s.player2Score ?? -1) &&
      _sigP2Lives === (s.player2Lives ?? -1) &&
      _sigP2Streak === (s.player2Streak ?? -1) &&
      _sigFlags === flags
    ) return;
    _sigScore = s.score; _sigStreak = s.streak ?? -1; _sigLives = s.lives ?? -1;
    _sigSecs = secs; _sigBestScore = s.bestScore ?? -1;
    _sigP2Score = s.player2Score ?? -1; _sigP2Lives = s.player2Lives ?? -1;
    _sigP2Streak = s.player2Streak ?? -1; _sigFlags = flags;
    const secsOrNull = secs === -1 ? null : secs;

    const splitFields = {
      split,
      player1Score: split ? s.score : null,
      player1Lives: split ? s.lives : null,
      player1Streak: split ? s.streak : null,
      player2Score: split ? s.player2Score : null,
      player2Lives: split ? s.player2Lives : null,
      player2Streak: split ? s.player2Streak : null,
      player1GameOver: s.player1GameOver,
      player2GameOver: s.player2GameOver,
    };

    const combo2Fields = {
      combo2Count: s.combo2?.count ?? null,
      combo2NormX: s.combo2?.x ?? null,
      combo2NormY: s.combo2?.y ?? null,
    };

    // In split CLASSIC: if only one player's lives ran out, suppress the global game-over overlay
    // and let the other player keep playing. Show their result as a small side badge instead.
    const onePlayerDoneOtherAlive =
      split &&
      (s.player1GameOver || s.player2GameOver) &&
      !(s.player1GameOver && s.player2GameOver);

    if (s.isGameOver && !onePlayerDoneOtherAlive) {
      setFruitNinjaRemoteUiSnapshot({
        hudVisible: false,
        pauseVisible: false,
        gameOverVisible: true,
        score: s.score,
        pauseScore: 0,
        gameOverScore: s.score,
        lives: s.lives,
        timeLeftSec: null,
        timerUrgent: false,
        gameMode: s.gameMode,
        streak: s.streak,
        comboCount: s.combo?.count ?? null,
        comboNormX: s.combo?.x ?? null,
        comboNormY: s.combo?.y ?? null,
        bestScore: s.bestScore,
        ...splitFields,
        ...combo2Fields,
      });
      return;
    }

    if (s.isPaused) {
      setFruitNinjaRemoteUiSnapshot({
        hudVisible: false,
        pauseVisible: true,
        gameOverVisible: false,
        score: s.score,
        pauseScore: s.score,
        gameOverScore: 0,
        lives: s.lives,
        timeLeftSec: secsOrNull,
        timerUrgent: secsOrNull !== null && secsOrNull <= 10,
        gameMode: s.gameMode,
        streak: s.streak,
        comboCount: s.combo?.count ?? null,
        comboNormX: s.combo?.x ?? null,
        comboNormY: s.combo?.y ?? null,
        bestScore: s.bestScore,
        ...splitFields,
        ...combo2Fields,
      });
      return;
    }

    setFruitNinjaRemoteUiSnapshot({
      hudVisible: true,
      pauseVisible: false,
      gameOverVisible: false,
      score: s.score,
      pauseScore: 0,
      gameOverScore: 0,
      lives: s.lives,
      timeLeftSec: secsOrNull,
      timerUrgent: secsOrNull !== null && secsOrNull <= 10,
      gameMode: s.gameMode,
      streak: s.streak,
      comboCount: s.combo?.count ?? null,
      comboNormX: s.combo?.x ?? null,
      comboNormY: s.combo?.y ?? null,
      bestScore: s.bestScore,
      ...splitFields,
      ...combo2Fields,
    });
  }

  function hideTargetAttackUi(): void {
    resetTargetAttackUi();
  }


  function hideLegKickUi(): void {
    resetLegKickUi();
  }

  function hideIqUi(): void {
    resetIqUi();
  }
  const qrImg = document.getElementById('qr-img') as HTMLImageElement;
  const roomCodeTiles = document.querySelectorAll<HTMLElement>('#room-code-tiles .lobby-code-tile');
  // `#webrtc-rtt` не рендерится на проде (см. TvPage) — пишем в отсоединённый элемент.
  const webrtcRttEl = document.getElementById('webrtc-rtt') ?? document.createElement('div');
  const webrtcStatsEl = document.getElementById('webrtc-stats') as HTMLElement | null;
  const tvSendLogsBtnEl = document.getElementById('tv-send-logs-btn') as HTMLButtonElement | null;
  const tvSendLogsLoaderEl = document.getElementById('tv-send-logs-loader') as HTMLElement | null;
  const tvSendLogsLoaderTextEl = document.getElementById('tv-send-logs-loader-text') as HTMLElement | null;
  const p2pWaitEl = document.getElementById('p2p-wait') as HTMLElement | null;
  const p2pWaitTitleEl = document.getElementById('p2p-wait-title') as HTMLElement | null;
  const p2pWaitSubEl = document.getElementById('p2p-wait-sub') as HTMLElement | null;
  const netToastEl = document.getElementById('net-toast') as HTMLElement | null;
  const netToastTextEl = document.getElementById('net-toast-text') as HTMLElement | null;
  const netToastDismissBtnEl = document.getElementById('net-toast-dismiss') as HTMLButtonElement | null;
  let netToastHideTimer: number | null = null;
  const playersStripEl = document.getElementById('players-strip') as HTMLElement | null;
  const playersStripNamesEl = document.getElementById('players-strip-names') as HTMLElement | null;
  const lobbyQrRefreshBtnEl = document.getElementById('lobby-qr-refresh-btn') as HTMLButtonElement | null;
  const lobbyPostQrWaitEl = document.getElementById('lobby-post-qr-wait') as HTMLElement | null;
  const lobbyPostQrRefreshBtnEl = document.getElementById('lobby-post-qr-refresh-btn') as
    | HTMLButtonElement
    | null;
  const gameUnsupportedOverlayEl = document.getElementById('game-unsupported-overlay') as HTMLElement | null;
  const lobbyChooseOverlayEl = document.getElementById('lobby-choose-overlay') as HTMLElement | null;
  const lobbyChooseOverlayIconEl = document.getElementById('lobby-choose-overlay-icon') as HTMLImageElement | null;
  const lobbyChooseOverlayTitleEl = document.getElementById('lobby-choose-overlay-title') as HTMLElement | null;
  const lobbyChooseOverlaySubEl = document.getElementById('lobby-choose-overlay-sub') as HTMLElement | null;
  const connectionLostOverlayEl = document.getElementById('connection-lost-overlay') as HTMLElement | null;
  const connectionLostReconnectBtnEl = document.getElementById('connection-lost-reconnect-btn') as HTMLButtonElement | null;
  const connectingOverlayEl = document.getElementById('connecting-overlay') as HTMLElement | null;
  const connectingOverlayTitleEl = document.getElementById('connecting-overlay-title') as HTMLElement | null;
  const connectingOverlaySubEl = document.getElementById('connecting-overlay-sub') as HTMLElement | null;
  const connectingOverlayCodeEl = document.getElementById('connecting-overlay-code') as HTMLElement | null;
  let connectingOverlayHideTimer: number | null = null;
  const roomBarEl = document.getElementById('room-bar') as HTMLElement | null;
  const lobbyHintModeEl = document.getElementById('lobby-hint-mode') as HTMLElement | null;
  let lobbyPostQrRefreshTimerId: number | null = null;
  /** Комната и анонимная сессия ТВ (`/api/public/v1/rooms`, `/api/tv/v1/*`, GEN-19020). */
  const tvRoomApi = new TvRoomApi();
  const tvLogSessionId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `tv-${Date.now()}-${Math.round(Math.random() * 1_000_000)}`;
  type TvClientLogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';
  type TvClientLogEntry = {
    ts: number;
    iso: string;
    level: TvClientLogLevel;
    args: string[];
  };
  const MAX_TV_LOG_BUFFER = 2000;
  const tvLogBuffer: TvClientLogEntry[] = [];
  let isSendingTvLogs = false;
  const nativeConsole = {
    log: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
    debug: console.debug.bind(console),
  };

  function stringifyLogArg(v: unknown): string {
    if (typeof v === 'string') return v;
    if (v instanceof Error) return `${v.name}: ${v.message}\n${v.stack ?? ''}`;
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }

  function pushTvClientLog(level: TvClientLogLevel, args: unknown[]): void {
    const entry: TvClientLogEntry = {
      ts: Date.now(),
      iso: new Date().toISOString(),
      level,
      args: args.map((a) => stringifyLogArg(a)),
    };
    tvLogBuffer.push(entry);
    if (tvLogBuffer.length > MAX_TV_LOG_BUFFER) {
      tvLogBuffer.splice(0, tvLogBuffer.length - MAX_TV_LOG_BUFFER);
    }
  }

  function setTvLogSendingUi(sending: boolean, text: string): void {
    if (tvSendLogsBtnEl) {
      tvSendLogsBtnEl.disabled = sending;
      if (!sending) tvSendLogsBtnEl.textContent = translate('tv.send_logs');
    }
    if (tvSendLogsLoaderEl) tvSendLogsLoaderEl.hidden = !sending;
    if (tvSendLogsLoaderTextEl) tvSendLogsLoaderTextEl.textContent = text;
  }

  async function sendTvClientLogs(): Promise<void> {
    if (isSendingTvLogs) return;
    isSendingTvLogs = true;
    setTvLogSendingUi(true, translate('tv.sending_logs'));
    try {
      const logs = tvLogBuffer.slice(-1000);
      const result = await tvRoomApi.sendClientLogs({
        roomCode: currentRoomCode || null,
        tvSessionId: tvLogSessionId,
        appVersion: 'tv-web',
        userAgent: navigator.userAgent,
        logs,
      });
      const saved = typeof result.saved === 'number' ? result.saved : logs.length;
      setTvLogSendingUi(true, translate('tv.log_sent', { n: saved }));
      window.setTimeout(() => setTvLogSendingUi(false, translate('tv.sending_logs')), 1200);
    } catch (err) {
      nativeConsole.error('[TV] failed to send client logs', err);
      setTvLogSendingUi(true, translate('tv.log_send_error'));
      window.setTimeout(() => setTvLogSendingUi(false, translate('tv.sending_logs')), 1500);
    } finally {
      isSendingTvLogs = false;
    }
  }

  console.log = (...args: unknown[]): void => {
    pushTvClientLog('log', args);
    nativeConsole.log(...args);
  };
  console.info = (...args: unknown[]): void => {
    pushTvClientLog('info', args);
    nativeConsole.info(...args);
  };
  console.warn = (...args: unknown[]): void => {
    pushTvClientLog('warn', args);
    nativeConsole.warn(...args);
  };
  console.error = (...args: unknown[]): void => {
    pushTvClientLog('error', args);
    nativeConsole.error(...args);
  };
  console.debug = (...args: unknown[]): void => {
    pushTvClientLog('debug', args);
    nativeConsole.debug(...args);
  };
  const onWindowError = (event: ErrorEvent): void => {
    pushTvClientLog('error', ['window.error', event.message, event.filename, event.lineno, event.colno]);
  };
  const onWindowUnhandledRejection = (event: PromiseRejectionEvent): void => {
    pushTvClientLog('error', ['window.unhandledrejection', stringifyLogArg(event.reason)]);
  };
  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onWindowUnhandledRejection);
  pushTvClientLog('info', ['tv logger initialized', `session=${tvLogSessionId}`]);

  function showConnectingOverlay(code: string): void {
    if (!connectingOverlayEl) return;
    connectingOverlayEl.classList.remove('connecting-overlay--success');
    if (connectingOverlayTitleEl) connectingOverlayTitleEl.textContent = translate('tv.connecting');
    if (connectingOverlaySubEl) connectingOverlaySubEl.hidden = false;
    if (connectingOverlayCodeEl) {
      connectingOverlayCodeEl.innerHTML = `${translate('tv.entered_code')}&nbsp;&nbsp;<strong>${code.toUpperCase()}</strong>`;
      connectingOverlayCodeEl.hidden = false;
    }
    connectingOverlayEl.hidden = false;
  }

  function showConnectingSuccess(): void {
    if (!connectingOverlayEl) return;
    connectingOverlayEl.classList.add('connecting-overlay--success');
    if (connectingOverlayTitleEl) connectingOverlayTitleEl.textContent = translate('tv.connected');
    if (connectingOverlayHideTimer !== null) window.clearTimeout(connectingOverlayHideTimer);
    connectingOverlayHideTimer = window.setTimeout(() => {
      if (connectingOverlayEl) connectingOverlayEl.hidden = true;
      connectingOverlayHideTimer = null;
    }, 3600);
  }

  function hideConnectingOverlay(): void {
    if (connectingOverlayHideTimer !== null) {
      window.clearTimeout(connectingOverlayHideTimer);
      connectingOverlayHideTimer = null;
    }
    if (connectingOverlayEl) connectingOverlayEl.hidden = true;
  }

  function showConnectionLostOverlay(): void {
    if (connectionLostOverlayEl) connectionLostOverlayEl.hidden = false;
  }

  function hideConnectionLostOverlay(): void {
    if (connectionLostOverlayEl) connectionLostOverlayEl.hidden = true;
  }

  connectionLostReconnectBtnEl?.addEventListener('click', () => {
    returnToLobby();
  });

  function clearLobbyPostQrRefreshTimer(): void {
    if (lobbyPostQrRefreshTimerId !== null) {
      window.clearTimeout(lobbyPostQrRefreshTimerId);
      lobbyPostQrRefreshTimerId = null;
    }
  }

  function hideLobbyPostQrWait(): void {
    clearLobbyPostQrRefreshTimer();
    if (lobbyPostQrWaitEl) lobbyPostQrWaitEl.hidden = true;
    if (lobbyPostQrRefreshBtnEl) lobbyPostQrRefreshBtnEl.hidden = true;
  }

  function showLobbyPostQrWait(): void {
    if (!lobbyPostQrWaitEl) return;
    lobbyPostQrWaitEl.hidden = false;
    if (lobbyPostQrRefreshBtnEl) lobbyPostQrRefreshBtnEl.hidden = true;
    clearLobbyPostQrRefreshTimer();
    lobbyPostQrRefreshTimerId = window.setTimeout(() => {
      if (lobbyPostQrRefreshBtnEl && !lobbyPostQrWaitEl.hidden) {
        lobbyPostQrRefreshBtnEl.hidden = false;
      }
    }, 10_000);
  }

  function isWaitingForLobbyP2p(): boolean {
    return connectedPlayers.size > 0 && activeGameId === null && webrtcManager.getActiveCount() === 0;
  }

  /** Единый переключатель этапов лобби: QR -> ожидание P2P -> выбор игры. */
  function syncLobbyStageVisibility(): void {
    const showJoin = connectedPlayers.size === 0;
    const showWait = isWaitingForLobbyP2p();
    if (roomBarEl) roomBarEl.hidden = !showJoin;
    if (showWait) showLobbyPostQrWait();
    else hideLobbyPostQrWait();
  }

  function syncLobbyPostQrWaitVisibility(): void {
    syncLobbyStageVisibility();
  }

  type CatchPoseOverlayApi = {
    attachStream: (stream: MediaStream | null) => void;
    setConnected: (connected: boolean) => void;
    setVisible: (visible: boolean) => void;
    getVideoEl?: () => HTMLVideoElement | null;
  };
  function getCatchPoseOverlayApi(): CatchPoseOverlayApi | null {
    try {
      const w = window as typeof window & { __catchPoseTvOverlayApi?: CatchPoseOverlayApi };
      return w.__catchPoseTvOverlayApi ?? null;
    } catch {
      return null;
    }
  }
  let lastCatchPoseVisible: boolean | null = null;
  let lastCatchPoseConnected: boolean | null = null;
  function setCatchPoseOverlayVisible(visible: boolean): void {
    if (lastCatchPoseVisible === visible) return;
    const api = getCatchPoseOverlayApi();
    if (!api) return;
    lastCatchPoseVisible = visible;
    api.setVisible(visible);
  }
  function setCatchPoseOverlayConnection(connected: boolean): void {
    if (lastCatchPoseConnected === connected) return;
    const api = getCatchPoseOverlayApi();
    if (!api) return;
    lastCatchPoseConnected = connected;
    api.setConnected(connected);
  }
  function setCatchPoseOverlayStream(stream: MediaStream | null): void {
    const api = getCatchPoseOverlayApi();
    if (!api) return;
    api.attachStream(stream);
  }
  
  /** Server `player:connected` / `player:disconnected` — id → display name */
  const connectedPlayers = new Map<string, string>();
  
  /** Timestamp when the first player connected via socket (used to detect WebRTC stall). */
  let playerConnectedAt: number | null = null;
  /** True after any peer reported `failed`, or offer handling threw. Reset on new player session. */
  let webrtcHasFailed = false;
  /** One automatic retry before force reset back to QR. */
  let webrtcRetryAttempted = false;
  /** Prevent repeated lobby reset while `P2P: нет связи` branch runs each second. */
  let resettingAfterP2pLoss = false;
  /** How long to wait for WebRTC to appear before flagging an error in the RTT overlay. */
  // TURN/relay setups can establish noticeably slower than direct LAN P2P.
  // По ТЗ: нет `connected` за 15 с после join → FAILED.
  const WEBRTC_TIMEOUT_MS = 15_000;
  /**
   * srflx/prflx с RTT ниже порога — разворот на своём роутере или очень короткий путь, показываем DIRECT.
   * В ТЗ было 20 мс; загруженный Wi-Fi даёт 30–80 мс даже в одной сети (замер iOS), для игры 50 мс незаметны.
   */
  const ICE_LOCAL_RTT_MS = 50;
  /** Вердикт по ICE пересчитываем раз в 5 с первые 30 с после connected, дальше не трогаем. */
  const ICE_VERDICT_INTERVAL_MS = 5_000;
  const ICE_VERDICT_WINDOW_MS = 30_000;
  /**
   * Remote-authoritative games do not enter calibration / playing until at least one
   * `RTCPeerConnection` is `connected` (DataChannel path). Set `false` to restore legacy
   * start-before-P2P behaviour (still need frames from somewhere if socket mirrors are off).
   */
  const TV_REMOTE_GAME_REQUIRES_P2P = true;
  
  /** QR + код комнаты — только пока нет игрока (шаг «ожидание подключения»). */
  function updateLobbyQrAndCodeVisibility(): void {
    syncLobbyStageVisibility();
  }
  
  function renderPlayersStrip(): void {
    if (playersStripEl && playersStripNamesEl) {
      if (connectedPlayers.size === 0) {
        playersStripNamesEl.textContent = translate('tv.waiting');
        playersStripEl.classList.add('players-strip--empty');
      } else {
        playersStripEl.classList.remove('players-strip--empty');
        const names: string[] = [];
        connectedPlayers.forEach((name) => names.push(name));
        playersStripNamesEl.textContent = names.join(' · ');
      }
    }
    syncLobbyStageVisibility();
  }
  
  /** Выбор игры в комнате (API / tv:joined), до команды старт — не путать с activeGameId */
  let roomSelectedGameId: string | null = null;
  let roomSelectedMode: GameMode | null = null;
  let runnerStagePicked = false;
  let roomSelectedPlayerCount: number | null = null;
  let roomSelectedGameSupported = true;
  
  const MODE_LABELS: Record<GameMode, () => string> = {
    classic: () => translate('game.classic'),
    arcade: () => translate('game.arcade'),
    zen: () => translate('game.zen'),
    iq: () => 'IQ',
  };
  const MODE_ICONS: Record<GameMode, string> = {
    classic: '⚔️',
    arcade: '🕹️',
    zen: '🧘',
    iq: '🧠',
  };
  
  /** Игры, которые headless-ТВ умеет вести сам (пилот — Sky Race). */
  const HEADLESS_GAMES = ['sky-race'] as const;

  function showHeadlessCatalog(): void {
    headlessMenu.show({
      key: 'catalog',
      title: translate('headless.menu_title'),
      subtitle: translate('headless.menu_sub'),
      buttons: HEADLESS_GAMES.map((id) => ({ id, label: getGameOrNull(id)?.title ?? id })),
      onPick: (gameId) => {
        // Сервер ответит `room:game-selected` — дальше как при выборе с телефона.
        void socket.tvSelectGame(gameId).then((ack) => {
          if (!ack?.ok) console.warn('[TV] tv:select-game failed', gameId, ack);
        });
      },
    });
  }

  function updateLobbyHints(): void {
    const hasPlayer = connectedPlayers.size >= 1;
    const waitForP2p = isWaitingForLobbyP2p();

    if (lobbyHintModeEl) lobbyHintModeEl.hidden = true;

    if (!hasPlayer || waitForP2p) {
      lobbyHintChooseEl.hidden = true;
      lobbyHintReadyEl.hidden = true;
      if (lobbyChooseOverlayEl) lobbyChooseOverlayEl.hidden = true;
      headlessMenu.hide();
      return;
    }

    // Headless: вместо «выберите игру на телефоне» — каталог на ТВ; дальше экраны ведёт сама игра.
    if (headlessPlayerId !== null) {
      lobbyHintChooseEl.hidden = true;
      lobbyHintReadyEl.hidden = true;
      if (lobbyChooseOverlayEl) lobbyChooseOverlayEl.hidden = true;
      if (!roomSelectedGameId) showHeadlessCatalog();
      else if (headlessMenu.screenKey === 'catalog') headlessMenu.hide();
      return;
    }

    if (!roomSelectedGameId) {
      lobbyHintChooseEl.hidden = true;
      lobbyHintReadyEl.hidden = true;
      if (lobbyChooseOverlayEl) {
        lobbyChooseOverlayEl.hidden = false;
        lobbyChooseOverlayEl.classList.add('lobby-choose-overlay--wall');
        if (lobbyChooseOverlayIconEl) lobbyChooseOverlayIconEl.src = '/icons/Phone.svg';
        if (lobbyChooseOverlayTitleEl) lobbyChooseOverlayTitleEl.textContent = translate('tv.choose_game_title');
        if (lobbyChooseOverlaySubEl) lobbyChooseOverlaySubEl.textContent = translate('tv.choose_game_sub');
      }
      if (lobbyHintModeEl) lobbyHintModeEl.hidden = true;
      return;
    }

    // У «Полёта птицы», Chef Master, Sky Race и IQ на ТВ нет режимов и числа игроков — ждать их нечего (режим Sky Race и выбор IQ идут в кадрах).
    if (!roomSelectedMode && !(roomSelectedGameId === 'runner' && runnerStagePicked) && roomSelectedGameId !== 'bird-flight' && roomSelectedGameId !== 'chef-master' && roomSelectedGameId !== 'sky-race' && roomSelectedGameId !== 'laser-dodge' && roomSelectedGameId !== 'iq-web') {
      lobbyHintChooseEl.hidden = true;
      lobbyHintReadyEl.hidden = true;
      if (lobbyChooseOverlayEl) {
        lobbyChooseOverlayEl.hidden = false;
        lobbyChooseOverlayEl.classList.remove('lobby-choose-overlay--wall');
        if (lobbyChooseOverlayIconEl) lobbyChooseOverlayIconEl.src = '/icons/Icon.svg';
        if (lobbyChooseOverlayTitleEl) lobbyChooseOverlayTitleEl.textContent = translate('tv.choose_mode_title');
        if (lobbyChooseOverlaySubEl) lobbyChooseOverlaySubEl.textContent = translate('tv.choose_mode_sub');
      }
      if (lobbyHintModeEl) lobbyHintModeEl.hidden = true;
      return;
    }

    if (lobbyChooseOverlayEl) lobbyChooseOverlayEl.hidden = true;

    // Mode selected — show summary + wait for start
    lobbyHintChooseEl.hidden = true;
    lobbyHintReadyEl.hidden = false;
    if (lobbyHintModeEl) lobbyHintModeEl.hidden = true;

    const game = roomSelectedGameId ? getGameOrNull(roomSelectedGameId) : null;
    lobbyHintReadyTitleEl.textContent = game ? `${game.title}` : translate('tv.game');

    if (roomSelectedGameId === 'runner' || roomSelectedGameId === 'bird-flight' || roomSelectedGameId === 'chef-master' || roomSelectedGameId === 'sky-race' || roomSelectedGameId === 'laser-dodge' || roomSelectedGameId === 'iq-web') {
      lobbyHintReadyTextEl.textContent = translate('tv.press_start');
    } else if (roomSelectedMode) {
      const modeLabel = MODE_LABELS[roomSelectedMode]();
      const modeIcon = MODE_ICONS[roomSelectedMode];
      const players = roomSelectedPlayerCount ?? 1;
      const playersText = translate(players === 1 ? 'tv.players_one' : 'tv.players_many', { n: players });
      const summary = translate('tv.mode_summary', { mode: modeLabel, players: playersText });
      const isRemote = roomSelectedGameId !== null && isRemoteGame(roomSelectedGameId);
      lobbyHintReadyTextEl.textContent =
        `${modeIcon} ${summary} ${translate(isRemote ? 'tv.pose_to_start' : 'tv.press_start')}`;
      if (lobbyHintModeEl) lobbyHintModeEl.hidden = false;
    }
  }
  let remoteEngine!: RemoteFruitDisplayEngine;
  let targetAttackEngine!: TargetAttackDisplayEngine;
  let legKickEngine!: LegKickDisplayEngine;
  let runnerEngine!: RunnerDisplayEngine;
  let iqEngine!: IqDisplayEngine;
  let birdFlightEngine!: BirdFlightDisplayEngine;
  let fruitSliceWebEngine!: FruitSliceWebDisplayEngine;
  let chefMasterEngine!: ChefMasterDisplayEngine;
  let skyRaceEngine!: SkyRaceDisplayEngine;
  let laserDodgeEngine!: LaserDodgeDisplayEngine;
  let iqWebEngine!: IqWebDisplayEngine;
  
  // ── Canvas resize ─────────────────────────────────────────────────────────────
  function resizeCanvas(): void {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const { bufferW, bufferH, cssW, cssH } = computeTvCanvasBufferSize(vw, vh);
    const finalW = bufferW;
    const finalH = bufferH;
    canvas.width = finalW;
    canvas.height = finalH;
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
    if (remoteEngine) remoteEngine.setCanvasSize(finalW, finalH);
    if (targetAttackEngine) targetAttackEngine.setCanvasSize(finalW, finalH);
    if (legKickEngine) legKickEngine.setCanvasSize(finalW, finalH);
    try {
      canvas.dataset.tvBufferCss = `${cssW}x${cssH}`;
      canvas.dataset.tvBufferPx = `${finalW}x${finalH}`;
    } catch {
      /* ignore */
    }
  }
  
  window.addEventListener('resize', resizeCanvas);
  
  // ── Wiring (DI) ───────────────────────────────────────────────────────────────
  // Профиль качества на основе фактического buffer/CSS размера canvas после resize.
  resizeCanvas();
  const tvQualityProfile = detectTvQualityProfile(canvas);
  const tvQualityConfig = getTvQualityConfig(tvQualityProfile);
  if (typeof document !== 'undefined' && document.body) {
    document.body.classList.toggle('tv-perf-low', tvQualityProfile === 'low');
    document.body.classList.toggle('tv-perf-medium', tvQualityProfile === 'medium');
    document.body.dataset.tvPerfProfile = tvQualityProfile;
  }
  const renderer = new CanvasRenderer(canvas, tvQualityConfig);
  remoteEngine = new RemoteFruitDisplayEngine(renderer, tvQualityConfig);
  targetAttackEngine = new TargetAttackDisplayEngine(renderer);
  legKickEngine = new LegKickDisplayEngine(renderer);
  runnerEngine = new RunnerDisplayEngine(canvas, tvQualityProfile);
  iqEngine = new IqDisplayEngine(renderer, canvas);
  birdFlightEngine = new BirdFlightDisplayEngine(tvQualityProfile);
  // Мир и счёт живут на ТВ — итог забега телефон узнаёт отсюда.
  birdFlightEngine.onFinished = (stars) => {
    socket.sendGameState({ state: 'gameover', score: stars });
  };
  // Телефон ждёт с калибровкой, пока ТВ строит карту, и показывает тот же процент.
  birdFlightEngine.onWorldLoading = (progress, map) => {
    socket.sendGameState({ state: 'loading', progress, map });
  };
  birdFlightEngine.onWorldReady = (map) => {
    socket.sendGameState({ state: 'ready', map });
  };
  // Fruit Slice Web (игра комнаты fruit-slice-web): игра и счёт на ТВ, телефон шлёт руки (кадр FruitSliceWebData).
  fruitSliceWebEngine = new FruitSliceWebDisplayEngine();
  fruitSliceWebEngine.onScore = (scores) => {
    socket.sendGameState({ state: 'playing', scores });
  };
  fruitSliceWebEngine.onFinished = (scores) => {
    socket.sendGameState({ state: 'gameover', score: Math.max(...scores), scores });
  };
  chefMasterEngine = new ChefMasterDisplayEngine();
  // Правила и счёт живут в странице на ТВ — итог забега телефон узнаёт отсюда.
  chefMasterEngine.onFinished = (score) => {
    socket.sendGameState({ state: 'gameover', score });
  };
  skyRaceEngine = new SkyRaceDisplayEngine(tvQualityProfile);
  // Правила и счёт живут в странице на ТВ — итог забега (звёзды, метры) телефон узнаёт отсюда.
  skyRaceEngine.onFinished = (stars, distance) => {
    // Headless: итог один раз за забег и экран «Ещё раз / В меню» — у контроллера.
    if (headlessSkyRace.isActive()) headlessSkyRace.onFinished(stars, distance);
    else socket.sendGameState({ state: 'gameover', score: stars, distance });
  };
  // Laser Dodge: правила и счёт на телефоне, ТВ только рисует его кадры (LaserDodgeData) — итог слать некуда.
  laserDodgeEngine = new LaserDodgeDisplayEngine();
  // IQ на ТВ (iq-web): страница считает раунд на ТВ — итог (очки, верные, всего, время) телефон узнаёт отсюда и пишет в сессию.
  iqWebEngine = new IqWebDisplayEngine();
  iqWebEngine.onFinished = ({ score, correct, total, durationSec }) => {
    socket.sendGameState({ state: 'gameover', score, correct, total, durationSec });
  };
  // ── Headless (GEN-18912): телефон шлёт только позу, игры выбираются и идут на ТВ ──
  /** Поза headless-телефона (`BodyPoseData`), сглаженная; `current()` — null, если устарела. */
  const bodyPose = new BodyPoseStore();
  /** Каталог, выбор режима и итог — кнопки с пультом и рукой-курсором. */
  const headlessMenu = new HeadlessMenuOverlay((now) => bodyPose.current(now));
  /** Подключённый headless-телефон (комната держит одно устройство). */
  let headlessPlayerId: string | null = null;
  const headlessSkyRace = new HeadlessSkyRaceController({
    menu: headlessMenu,
    engine: skyRaceEngine,
    getPose: (now) => bodyPose.current(now),
    startGame: () => socket.tvStartGame('sky-race'),
    exitGame: () => socket.tvExitGame(),
    reportGameOver: (stars, distance) => socket.sendGameState({ state: 'gameover', score: stars, distance }),
  });
  /** Скелет игрока на ТВ: в меню, на калибровке и в итоге; пока идёт забег — скрыт. */
  const headlessSkeleton = new HeadlessSkeletonOverlay((now) => bodyPose.current(now), () => headlessSkyRace.isGameplay());
  const fruitSoundEngine = new SoundManager();
  const targetAttackSoundEngine = new TargetAttackSoundEngine();
  const legKickSoundEngine = new LegKickSoundEngine();
  remoteEngine.setSoundManager(fruitSoundEngine);
  targetAttackEngine.setSoundEngine(targetAttackSoundEngine);
  legKickEngine.setSoundEngine(legKickSoundEngine);
  let catchPosePhase: 'lobby' | 'playing' = 'lobby';
  let catchPoseStream: MediaStream | null = null;

  function syncTargetAttackUi(): void {
    const s = targetAttackEngine.getState();
    const phase = s.phase;

    if (phase !== 'playing' && phase !== 'calibration') {
      hideTargetAttackUi();
      return;
    }

    if (phase === 'calibration') {
      hideTargetAttackUi();
      return;
    }

    const split =
      s.splitTwoPlayers && s.player1Score !== null && s.player2Score !== null;
    const secs = s.timeLeft !== null ? Math.ceil(s.timeLeft) : null;

    if (s.isGameOver) {
      setTargetAttackUiSnapshot({
        hudVisible: false,
        pauseVisible: false,
        gameOverVisible: true,
        score: s.score,
        split,
        player1Score: split ? s.player1Score : null,
        player2Score: split ? s.player2Score : null,
        pauseSplit: false,
        pauseScoreSingle: 0,
        pauseP1: null,
        pauseP2: null,
        lives: s.lives,
        timeLeftSec: null,
        timerUrgent: false,
        gameMode: s.gameMode,
        streak: s.streak,
        comboCount: s.combo?.count ?? null,
        currentLevel: s.currentLevel,
        levelProgress: s.levelProgress,
      });
      return;
    }

    if (s.isPaused) {
      const pSplit =
        s.splitTwoPlayers && s.player1Score !== null && s.player2Score !== null;
      setTargetAttackUiSnapshot({
        hudVisible: false,
        pauseVisible: true,
        gameOverVisible: false,
        score: s.score,
        split,
        player1Score: split ? s.player1Score : null,
        player2Score: split ? s.player2Score : null,
        pauseSplit: pSplit,
        pauseScoreSingle: s.score,
        pauseP1: pSplit ? s.player1Score : null,
        pauseP2: pSplit ? s.player2Score : null,
        lives: s.lives,
        timeLeftSec: secs,
        timerUrgent: secs !== null && secs <= 10,
        gameMode: s.gameMode,
        streak: s.streak,
        comboCount: s.combo?.count ?? null,
        currentLevel: s.currentLevel,
        levelProgress: s.levelProgress,
      });
      return;
    }

    setTargetAttackUiSnapshot({
      hudVisible: true,
      pauseVisible: false,
      gameOverVisible: false,
      score: s.score,
      split,
      player1Score: split ? s.player1Score : null,
      player2Score: split ? s.player2Score : null,
      pauseSplit: false,
      pauseScoreSingle: 0,
      pauseP1: null,
      pauseP2: null,
      lives: s.lives,
      timeLeftSec: secs,
      timerUrgent: secs !== null && secs <= 10,
      gameMode: s.gameMode,
      streak: s.streak,
      comboCount: s.combo?.count ?? null,
      currentLevel: s.currentLevel,
      levelProgress: s.levelProgress,
    });
  }
  

  function syncLegKickUi(): void {
    const s = legKickEngine.getState();
    const phase = s.phase;

    if (phase !== 'playing' && phase !== 'calibration') {
      hideLegKickUi();
      return;
    }

    if (phase === 'calibration') {
      hideLegKickUi();
      return;
    }

    const split =
      s.splitTwoPlayers && s.player1Score !== null && s.player2Score !== null;
    const secs = s.timeLeft !== null ? Math.ceil(s.timeLeft) : null;

    if (s.isGameOver) {
      setLegKickUiSnapshot({
        hudVisible: false,
        pauseVisible: false,
        gameOverVisible: true,
        score: s.score,
        split,
        player1Score: split ? s.player1Score : null,
        player2Score: split ? s.player2Score : null,
        pauseSplit: false,
        pauseScoreSingle: 0,
        pauseP1: null,
        pauseP2: null,
        lives: s.lives,
        timeLeftSec: null,
        timerUrgent: false,
        gameMode: s.gameMode,
        streak: s.streak,
        comboCount: s.combo?.count ?? null,
        currentLevel: s.currentLevel,
        levelProgress: s.levelProgress,
      });
      return;
    }

    if (s.isPaused) {
      const pSplit =
        s.splitTwoPlayers && s.player1Score !== null && s.player2Score !== null;
      setLegKickUiSnapshot({
        hudVisible: false,
        pauseVisible: true,
        gameOverVisible: false,
        score: s.score,
        split,
        player1Score: split ? s.player1Score : null,
        player2Score: split ? s.player2Score : null,
        pauseSplit: pSplit,
        pauseScoreSingle: s.score,
        pauseP1: pSplit ? s.player1Score : null,
        pauseP2: pSplit ? s.player2Score : null,
        lives: s.lives,
        timeLeftSec: secs,
        timerUrgent: secs !== null && secs <= 10,
        gameMode: s.gameMode,
        streak: s.streak,
        comboCount: s.combo?.count ?? null,
        currentLevel: s.currentLevel,
        levelProgress: s.levelProgress,
      });
      return;
    }

    setLegKickUiSnapshot({
      hudVisible: true,
      pauseVisible: false,
      gameOverVisible: false,
      score: s.score,
      split,
      player1Score: split ? s.player1Score : null,
      player2Score: split ? s.player2Score : null,
      pauseSplit: false,
      pauseScoreSingle: 0,
      pauseP1: null,
      pauseP2: null,
      lives: s.lives,
      timeLeftSec: secs,
      timerUrgent: secs !== null && secs <= 10,
      gameMode: s.gameMode,
      streak: s.streak,
      comboCount: s.combo?.count ?? null,
      currentLevel: s.currentLevel,
      levelProgress: s.levelProgress,
    });
  }

  
  function syncIqUi(): void {
    const s = iqEngine.getState();
    const phase = s.phase;

    if (phase === 'lobby' || phase === 'calibrating') {
      hideIqUi();
      return;
    }

    if (phase === 'gameover') {
      setIqUiSnapshot({
        hudVisible: false,
        pauseVisible: false,
        gameOverVisible: true,
        score: s.score,
        progress: s.progress,
        timeLeft: s.timeLeft,
        question: s.question,
        answerTextSize: s.answerTextSize,
        answers: s.answers.map((a) => ({
          id: a.id,
          x: a.nx,
          y: a.ny,
          w: a.nw,
          h: a.nh,
          text: a.text,
          backgroundColor: a.backgroundColor,
        })),
        labels: s.labels.map((l) => ({
          text: l.text,
          x: l.nx,
          y: l.ny,
          color: l.color,
          alpha: l.alpha,
        })),
      });
      return;
    }

    if (phase === 'paused') {
      setIqUiSnapshot({
        hudVisible: false,
        pauseVisible: true,
        gameOverVisible: false,
        score: s.score,
        progress: s.progress,
        timeLeft: s.timeLeft,
        question: s.question,
        answerTextSize: s.answerTextSize,
        answers: [],
        labels: [],
      });
      return;
    }

    setIqUiSnapshot({
      hudVisible: true,
      pauseVisible: false,
      gameOverVisible: false,
      score: s.score,
      progress: s.progress,
      timeLeft: s.timeLeft,
      question: s.question,
      answerTextSize: s.answerTextSize,
      answers: s.answers.map((a) => ({
        id: a.id,
        x: a.nx,
        y: a.ny,
        w: a.nw,
        h: a.nh,
        text: a.text,
        backgroundColor: a.backgroundColor,
      })),
      labels: s.labels.map((l) => ({
        text: l.text,
        x: l.nx,
        y: l.ny,
        color: l.color,
        alpha: l.alpha,
      })),
    });
  }

  const poseBridge = new PoseConsumerBridge();

  const socket = new TvSocketAdapter(poseBridge);
  let signalingConnected = socket.isConnected();
  
  socket.onConnect(() => {
    signalingConnected = true;
    fruitSoundEngine.retryAudioUnlock();
    targetAttackSoundEngine.retryAudioUnlock();
    legKickSoundEngine.retryAudioUnlock();
  });
  socket.onDisconnect((reason) => {
    signalingConnected = false;
    console.warn('[TV] signaling disconnected', reason);
  });
  
  socket.onPlayerConnected((p) => {
    const isFirst = connectedPlayers.size === 0;
    connectedPlayers.set(p.id, p.name);
    // Всегда присваиваем: id headless-телефона из закрытой комнаты не должен делать «headless» следующего обычного.
    headlessPlayerId = p.clientMode === 'headless' ? p.id : null;
    if (headlessPlayerId === null) headlessMenu.hide();
    headlessSkeleton.setEnabled(headlessPlayerId !== null);
    if (playerConnectedAt === null) {
      playerConnectedAt = Date.now();
      webrtcHasFailed = false;
      webrtcRetryAttempted = false;
    }
    if (isFirst) {
      showConnectingOverlay(currentRoomCode);
      window.setTimeout(() => showConnectingSuccess(), 800);
    }
    renderPlayersStrip();
    updateLobbyHints();
    syncLobbyPostQrWaitVisibility();
    tryAutoCalibrate();
  });
  
  socket.onPlayerDisconnected((p) => {
    connectedPlayers.delete(p.id);
    if (p.id === headlessPlayerId) {
      // Сервер уже сбросил выбор игры: обратно в лобби с QR, страница игры не остаётся висеть.
      resetHeadlessState();
      roomSelectedGameId = null;
      roomSelectedMode = null;
      roomSelectedPlayerCount = null;
      if (activeGameId !== null) stopGameAndShowLobby();
    }
    sentConnectionStatus.delete(p.id);
    timeToConnectedMs.delete(p.id);
    minIceRttMs.delete(p.id);
    // Телефон после реконнекта сокета приходит с новым id — старый пир иначе висит «connected».
    webrtcManager.closePeer(p.id);
    if (connectedPlayers.size === 0) {
      playerConnectedAt = null;
      webrtcHasFailed = false;
      webrtcRetryAttempted = false;
      cancelPendingRemoteStart();
      resetConnectionStatus();
    }
    renderPlayersStrip();
    updateLobbyHints();
    syncLobbyPostQrWaitVisibility();
    // Если последний игрок вышел во время активной игры/калибровки — показать экран разрыва соединения.
    if (connectedPlayers.size === 0 && activeGameId !== null) {
      const activePhase =
        activeGameId === 'target-attack'
          ? targetAttackEngine.getPhase()
          : activeGameId === 'leg-kick'
            ? legKickEngine.getPhase()
            : activeGameId === 'catch-pose'
              ? catchPosePhase
              : activeGameId === 'bird-flight'
                ? birdFlightEngine.getPhase()
                : activeGameId === 'chef-master'
                  ? chefMasterEngine.getPhase()
                : activeGameId === 'sky-race'
                  ? skyRaceEngine.getPhase()
                : activeGameId === 'laser-dodge'
                  ? laserDodgeEngine.getPhase()
                : activeGameId === 'iq-web'
                  ? iqWebEngine.getPhase()
                : activeGameId === 'fruit-slice-web'
                  ? fruitSliceWebEngine.getPhase()
                  : remoteEngine.getPhase();
      if (activePhase === 'playing' || activePhase === 'calibration') {
        showConnectionLostOverlay();
        // IQ: раунд стоит, пока поверх экран разрыва (вернётся телефон — кадры снимут паузу).
        if (activeGameId === 'iq-web') iqWebEngine.pauseForDisconnect();
      }
    }
  });
  
  const webrtcManager = new WebRTCManager();
  
  let currentRoomCode = '';
  /**
   * Sticky room lock for this TV tab/session.
   * After the first successful `POST /api/public/v1/rooms`, reconnects should re-use the same room
   * instead of creating a brand-new code (prevents room churn under transient transport drops).
   * A new code is generated only on full page reload / explicit manual refresh.
   */
  let lockedRoomCode: string | null = null;
  /** Which game the server last told us to run (API / socket). */
  let activeGameId: string | null = null;
  
  function isRemoteGame(gameId: string): boolean {
    return gameId === 'fruit-ninja-without-score' || gameId === 'target-attack' || gameId === 'catch-pose' || gameId === 'leg-kick' || gameId === 'runner' || gameId === 'iq' || gameId === 'bird-flight' || gameId === 'chef-master' || gameId === 'fruit-slice-web' || gameId === 'sky-race' || gameId === 'laser-dodge' || gameId === 'iq-web';
  }

  type RemoteAuthoritativeId = 'fruit-ninja-without-score' | 'target-attack' | 'catch-pose' | 'leg-kick' | 'runner' | 'iq' | 'bird-flight' | 'chef-master' | 'fruit-slice-web' | 'sky-race' | 'laser-dodge' | 'iq-web';
  type PendingRemoteStart =
    | null
    | { kind: 'calibrate'; gameId: RemoteAuthoritativeId }
    | { kind: 'phoneStart'; gameId: RemoteAuthoritativeId };

  let pendingRemoteStart: PendingRemoteStart = null;
  /** Browser TV: timer id is a number (Node typings use `Timeout`). */
  let p2pWaitPollId: number | null = null;
  let p2pWaitSince: number | null = null;

  function clearP2pWaitPoll(): void {
    if (p2pWaitPollId !== null) {
      clearInterval(p2pWaitPollId);
      p2pWaitPollId = null;
    }
  }

  function hideP2pWaitOverlay(): void {
    clearP2pWaitPoll();
    p2pWaitSince = null;
    if (p2pWaitEl) p2pWaitEl.hidden = true;
  }

  /** Тост с кнопкой «Понятно» — висит, пока не закроют пультом (OK/Back). */
  let netToastDismissible = false;

  function showNetToast(
    message: string,
    autohideMs = 0,
    severity: 'bad' | 'warn' | 'relay' | 'good' = 'bad',
    dismissible = false,
  ): void {
    if (!netToastEl || !netToastTextEl) return;
    if (netToastHideTimer !== null) { window.clearTimeout(netToastHideTimer); netToastHideTimer = null; }
    netToastTextEl.textContent = message;
    netToastEl.classList.toggle('net-toast--warn', severity === 'warn');
    netToastEl.classList.toggle('net-toast--relay', severity === 'relay');
    netToastEl.classList.toggle('net-toast--good', severity === 'good');
    netToastDismissible = dismissible;
    if (netToastDismissBtnEl) netToastDismissBtnEl.hidden = !dismissible;
    netToastEl.hidden = false;
    // Trigger transition on next frame
    requestAnimationFrame(() => netToastEl.classList.add('net-toast--visible'));
    // Фокус на «Понятно» только вне игры — во время партии пульт не перехватываем.
    if (dismissible && activeGameId === null) netToastDismissBtnEl?.focus();
    if (autohideMs > 0) {
      netToastHideTimer = window.setTimeout(() => hideNetToast(), autohideMs);
    }
  }

  function hideNetToast(): void {
    if (!netToastEl) return;
    netToastDismissible = false;
    netToastEl.classList.remove('net-toast--visible');
    if (document.activeElement === netToastDismissBtnEl) netToastDismissBtnEl?.blur();
    netToastHideTimer = window.setTimeout(() => {
      if (netToastEl) netToastEl.hidden = true;
    }, 400);
  }

  // ── Статус соединения (ТЗ «проверка сети»): network:check + вердикт по ICE-паре ──
  /** Последний результат сравнения публичных IP от сервера. */
  let ipCheckResult: NetworkCheckResult | null = null;
  /** Сравнение подсетей при общем публичном IP: `different` — ТВ и телефон за разными роутерами. */
  let lanCheckResult: LanCheckResult | null = null;
  /** Худший тип выбранной ICE-пары среди игроков — для дебаг-панели. */
  let lastIcePair: IcePairType | null = null;
  /** Статус, который сейчас показан баннером на ТВ. */
  let tvConnectionStatus: ConnectionStatus | null = null;
  /** Статус, баннер которого закрыли «Понятно» — повторно не показываем, пока статус не сменится. */
  let dismissedConnectionStatus: ConnectionStatus | null = null;
  /** Последний статус, отправленный каждому телефону. */
  const sentConnectionStatus = new Map<string, ConnectionStatus>();
  /** Время от `player:connected` до P2P `connected`, по игрокам. */
  const timeToConnectedMs = new Map<string, number>();
  /** Начало окна пересчёта вердикта (connected / смена пары); null — окно закрыто. */
  let iceVerdictWindowStart: number | null = null;
  let iceVerdictLastEvalAt = 0;
  /** Пересчёт уже идёт — следующий запрос выполнится после него (иначе два параллельных getStats отправляют вердикт дважды). */
  let iceVerdictRunning = false;
  let iceVerdictRerun = false;

  const STATUS_RANK: Record<ConnectionStatus, number> = {
    CONNECTING: 0,
    DIRECT: 1,
    VIA_INTERNET: 2,
    SAME_NETWORK_BLOCKED: 3,
    DIFFERENT_NETWORK: 4,
    VIA_RELAY: 5,
    FAILED: 6,
  };

  /** Минимальный RTT выбранной пары по игрокам — джиттер Wi-Fi не должен переключать DIRECT ↔ VIA_INTERNET. */
  const minIceRttMs = new Map<string, number>();

  function statusFromIcePair(playerId: string, info: IcePairInfo): ConnectionStatus | null {
    if (info.pair === null) return null;
    if (info.pair === 'relay') return ipCheckResult === 'same' ? 'SAME_NETWORK_BLOCKED' : 'VIA_RELAY';
    if (info.pair === 'host') return 'DIRECT';
    if (info.rttMs !== null) {
      minIceRttMs.set(playerId, Math.min(info.rttMs, minIceRttMs.get(playerId) ?? Infinity));
    }
    const rttMs = minIceRttMs.get(playerId) ?? null;
    // Первый getStats сразу после connected часто без RTT — ждём следующий замер, а не пугаем «через интернет».
    if (rttMs === null) {
      const windowAgeMs = iceVerdictWindowStart !== null ? Date.now() - iceVerdictWindowStart : Infinity;
      if (windowAgeMs < ICE_VERDICT_INTERVAL_MS) return null;
      return ipCheckResult === 'same' ? 'DIRECT' : 'VIA_INTERNET';
    }
    // srflx / prflx: при малом RTT трафик фактически локальный — не пугаем.
    if (rttMs < ICE_LOCAL_RTT_MS) return 'DIRECT';
    // Общий публичный IP, но RTT большой — разворот через роутер между разными сетями (гостевая / второй роутер).
    return ipCheckResult === 'same' ? 'SAME_NETWORK_BLOCKED' : 'VIA_INTERNET';
  }

  function showConnectionStatusBanner(status: ConnectionStatus): void {
    switch (status) {
      case 'DIRECT':
        showNetToast(translate('tv.net.direct'), 3_000, 'good');
        return;
      case 'VIA_INTERNET':
        showNetToast(translate('tv.net.internet'), 8_000, 'warn');
        return;
      case 'DIFFERENT_NETWORK':
        showNetToast(translate('tv.net.different'), 0, 'warn', true);
        return;
      case 'VIA_RELAY':
        showNetToast(translate('tv.net.relay'), 0, 'relay', true);
        return;
      case 'SAME_NETWORK_BLOCKED':
        // Пользователь сам это обычно не починит — информируем и прячем, без кнопки «Понятно».
        showNetToast(
          translate(lanCheckResult === 'different' ? 'tv.net.different_lan' : 'tv.net.same_network_blocked'),
          8_000,
          'warn',
        );
        return;
      case 'FAILED':
        showNetToast(translate('tv.net.failed'), 0, 'bad', true);
        return;
      case 'CONNECTING':
        return;
    }
  }

  function setTvConnectionStatus(status: ConnectionStatus): void {
    if (status === tvConnectionStatus) return;
    tvConnectionStatus = status;
    if (status === dismissedConnectionStatus) return;
    dismissedConnectionStatus = null;
    showConnectionStatusBanner(status);
  }

  function dismissConnectionBanner(): void {
    if (!netToastDismissible) return;
    dismissedConnectionStatus = tvConnectionStatus;
    hideNetToast();
  }

  function sendConnectionStatus(playerId: string, status: ConnectionStatus, info: IcePairInfo | null): void {
    if (sentConnectionStatus.get(playerId) === status) return;
    sentConnectionStatus.set(playerId, status);
    console.log(
      '[WebRTC][TV] connection status',
      playerId,
      `status=${status}`,
      `pair=${info?.pair ?? '-'}`,
      `relay=${info?.relayProtocol ?? '-'}`,
      `rtt=${info?.rttMs != null ? Math.round(info.rttMs) : '-'}ms`,
    );
    socket.sendConnectionQuality({
      playerId,
      status,
      pair: info?.pair ?? null,
      rttMs: info?.rttMs != null ? Math.round(info.rttMs) : null,
      timeToConnectedMs: timeToConnectedMs.get(playerId) ?? null,
    });
  }

  function resetConnectionStatus(): void {
    ipCheckResult = null;
    lanCheckResult = null;
    lastIcePair = null;
    tvConnectionStatus = null;
    dismissedConnectionStatus = null;
    sentConnectionStatus.clear();
    timeToConnectedMs.clear();
    minIceRttMs.clear();
    iceVerdictWindowStart = null;
    hideNetToast();
  }

  function startIceVerdictWindow(): void {
    iceVerdictWindowStart = Date.now();
    iceVerdictLastEvalAt = 0;
    requestIceVerdict();
  }

  function requestIceVerdict(): void {
    if (iceVerdictRunning) {
      iceVerdictRerun = true;
      return;
    }
    iceVerdictRunning = true;
    void evaluateIceVerdicts().finally(() => {
      iceVerdictRunning = false;
      if (iceVerdictRerun) {
        iceVerdictRerun = false;
        requestIceVerdict();
      }
    });
  }

  /** Вердикт по выбранной ICE-паре каждого игрока → телефону; на ТВ — худший из них. */
  async function evaluateIceVerdicts(): Promise<void> {
    iceVerdictLastEvalAt = Date.now();
    let worst: ConnectionStatus | null = null;
    let worstPair: IcePairType | null = null;
    for (const playerId of connectedPlayers.keys()) {
      const info = await webrtcManager.getIcePair(playerId);
      if (!info) continue;
      const status = statusFromIcePair(playerId, info);
      if (status === null) continue;
      sendConnectionStatus(playerId, status, info);
      if (worst === null || STATUS_RANK[status] > STATUS_RANK[worst]) {
        worst = status;
        worstPair = info.pair;
      }
    }
    if (worst === null) return;
    lastIcePair = worstPair;
    setTvConnectionStatus(worst);
  }

  /** Вызывается раз в секунду из stats-цикла: пересчёт раз в 5 с внутри 30-секундного окна. */
  function tickIceVerdictWindow(): void {
    if (iceVerdictWindowStart === null) return;
    const now = Date.now();
    if (now - iceVerdictWindowStart > ICE_VERDICT_WINDOW_MS) {
      iceVerdictWindowStart = null;
      return;
    }
    if (now - iceVerdictLastEvalAt >= ICE_VERDICT_INTERVAL_MS) requestIceVerdict();
  }

  /** Отправляет свой IPv4 на IPv4-only хост — сервер сравнит с телефоном. */
  function reportIpv4(url: string): void {
    const socketId = socket.getSocketId();
    if (!socketId || !currentRoomCode) return;
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: currentRoomCode, socketId }),
    }).catch((err: unknown) => {
      console.warn('[TV] ipv4 report failed', err);
    });
  }

  function showP2pWaitOverlay(title: string, sub: string): void {
    if (p2pWaitTitleEl) p2pWaitTitleEl.textContent = title;
    if (p2pWaitSubEl) p2pWaitSubEl.textContent = sub;
    if (p2pWaitEl) p2pWaitEl.hidden = false;
  }

  function cancelPendingRemoteStart(): void {
    pendingRemoteStart = null;
    hideP2pWaitOverlay();
  }

  /** Картинки/звуки игры качаем со старта калибровки — на главном экране (QR) media не нужны. */
  function preloadGameMedia(gameId: RemoteAuthoritativeId): void {
    renderer.preloadGameMedia(gameId);
    if (gameId === 'fruit-ninja-without-score') fruitSoundEngine.preload();
    else if (gameId === 'target-attack') targetAttackSoundEngine.preload();
    else if (gameId === 'leg-kick') legKickSoundEngine.preload();
  }

  function executeRemoteCalibrationFromServer(gameId: RemoteAuthoritativeId): void {
    preloadGameMedia(gameId);
    activeGameId = gameId;
    lobbyEl.style.display = 'none';
    targetAttackEngine.setPhase('lobby');
    legKickEngine.setPhase('lobby');
    remoteEngine.setPhase('lobby');
    catchPosePhase = 'lobby';
    resetFruitNinjaRemoteUi();
    hideTargetAttackUi();
    hideLegKickUi();
    setCatchPoseOverlayVisible(gameId === 'catch-pose');
    setCatchPoseOverlayConnection(webrtcManager.getActiveCount() > 0);
    if (gameId === 'catch-pose') setCatchPoseOverlayStream(catchPoseStream);
    if (gameId === 'fruit-ninja-without-score') {
      remoteEngine.setPlayerCount(roomSelectedPlayerCount ?? 1);
      remoteEngine.startCalibration();
      remoteEngine.onCalibrationComplete = () => {
        socket.sendGameState({ state: 'playing' });
      };
      return;
    }
    if (gameId === 'catch-pose') {
      catchPosePhase = 'playing';
      socket.sendGameState({ state: 'playing' });
      return;
    }
    if (gameId === 'leg-kick') {
      legKickEngine.setPlayerCount(roomSelectedPlayerCount ?? 1);
      remoteEngine.setPlayerCount(roomSelectedPlayerCount ?? 1);
      legKickEngine.startCalibration();
      legKickEngine.onCalibrationComplete = () => {
        socket.sendGameState({ state: 'playing' });
      };
      return;
    }
    if (gameId === 'runner') {
      runnerEngine.startCalibration();
      runnerEngine.onCalibrationComplete = () => {
        socket.sendGameState({ state: 'playing' });
      };
      return;
    }
    if (gameId === 'iq') {
      iqEngine.startGame();
      socket.sendGameState({ state: 'playing' });
      return;
    }
    if (gameId === 'bird-flight') {
      // Калибровка идёт на телефоне; ТВ показывает мир и ждёт start-game.
      birdFlightEngine.startCalibration();
      return;
    }
    if (gameId === 'fruit-slice-web') {
      // Калибровка на телефоне; ТВ рисует рамки по его прогрессу, партия стартует по кадру STARTED.
      fruitSliceWebEngine.startCalibration();
      return;
    }
    if (gameId === 'chef-master') {
      // Калибровки нет: «покажи обе ладони» — часть страницы. ТВ поднимает её и ждёт start-game.
      chefMasterEngine.startCalibration();
      return;
    }
    if (gameId === 'sky-race') {
      // Headless: уровень, режим и калибровка — на ТВ, по позе.
      if (headlessPlayerId !== null) {
        headlessSkyRace.begin();
        return;
      }
      // Калибровка на телефоне; ТВ рисует рамку по её прогрессу и поднимает страницу по первому кадру (там режим).
      skyRaceEngine.startCalibration();
      return;
    }
    if (gameId === 'laser-dodge') {
      // Отсчёт «встань в силуэт» на телефоне; ТВ рисует рамку по его прогрессу, игра стартует по start-game.
      laserDodgeEngine.startCalibration();
      return;
    }
    if (gameId === 'iq-web') {
      // Тема и сложность выбраны на телефоне, калибровка тоже; ТВ рисует рамку по её прогрессу и поднимает страницу
      // по первому кадру (там выбор). Игра стартует по start-game.
      iqWebEngine.startCalibration();
      return;
    }
    targetAttackEngine.setPlayerCount(roomSelectedPlayerCount ?? 1);
    remoteEngine.setPlayerCount(roomSelectedPlayerCount ?? 1);
    targetAttackEngine.startCalibration();
    targetAttackEngine.onCalibrationComplete = () => {
      socket.sendGameState({ state: 'playing' });
    };
  }

  function executeRemotePlayingFromPhone(gameId: RemoteAuthoritativeId): void {
    // Обычно уже загружено на калибровке; здесь — если телефон стартовал игру без неё.
    preloadGameMedia(gameId);
    activeGameId = gameId;
    lobbyEl.style.display = 'none';
    resetFruitNinjaRemoteUi();
    hideTargetAttackUi();
    hideLegKickUi();
    catchPosePhase = 'lobby';
    remoteEngine.setPhase('lobby');
    targetAttackEngine.setPhase('lobby');
    legKickEngine.setPhase('lobby');
    setCatchPoseOverlayVisible(gameId === 'catch-pose');
    setCatchPoseOverlayConnection(webrtcManager.getActiveCount() > 0);
    if (gameId === 'catch-pose') setCatchPoseOverlayStream(catchPoseStream);
    if (gameId === 'fruit-ninja-without-score') {
      remoteEngine.setPlayerCount(roomSelectedPlayerCount ?? 1);
      remoteEngine.startGame();
    } else if (gameId === 'catch-pose') {
      catchPosePhase = 'playing';
    } else if (gameId === 'leg-kick') {
      console.log('[TV] Starting leg-kick game');
      legKickEngine.startGame();
    } else if (gameId === 'runner') {
      console.log('[TV] Starting runner game');
      runnerEngine.startGame();
    } else if (gameId === 'iq') {
      console.log('[TV] Starting iq game');
      iqEngine.startGame();
    } else if (gameId === 'bird-flight') {
      console.log('[TV] Starting bird-flight game');
      birdFlightEngine.startGame();
    } else if (gameId === 'chef-master') {
      console.log('[TV] Starting chef-master game');
      chefMasterEngine.startGame();
    } else if (gameId === 'sky-race') {
      console.log('[TV] Starting sky-race game');
      skyRaceEngine.startGame();
      headlessSkyRace.onStarted();
    } else if (gameId === 'laser-dodge') {
      console.log('[TV] Starting laser-dodge game');
      laserDodgeEngine.startGame();
    } else if (gameId === 'iq-web') {
      console.log('[TV] Starting iq-web game');
      iqWebEngine.startGame();
    } else if (gameId === 'fruit-slice-web') {
      console.log('[TV] Starting fruit-slice-web game');
      fruitSliceWebEngine.startGame();
    } else {
      console.log('[TV] Starting target-attack game');
      targetAttackEngine.startGame();
    }
    socket.sendGameState({ state: 'playing' });
  }

  function flushPendingRemoteStartIfReady(): void {
    if (!pendingRemoteStart) return;
    if (webrtcManager.getActiveCount() === 0) return;
    const p = pendingRemoteStart;
    pendingRemoteStart = null;
    hideP2pWaitOverlay();
    if (p.kind === 'calibrate') executeRemoteCalibrationFromServer(p.gameId);
    else executeRemotePlayingFromPhone(p.gameId);
  }

  function scheduleRemoteStartUntilP2p(p: NonNullable<PendingRemoteStart>): void {
    pendingRemoteStart = p;
    webrtcHasFailed = false;
    p2pWaitSince = Date.now();
    showP2pWaitOverlay(
      translate('tv.p2p_connecting'),
      translate('tv.p2p_connecting_sub'),
    );
    clearP2pWaitPoll();
    p2pWaitPollId = window.setInterval(() => {
      if (!pendingRemoteStart) {
        hideP2pWaitOverlay();
        return;
      }
      if (webrtcHasFailed) {
        showP2pWaitOverlay(
          translate('tv.p2p_failed'),
          translate('tv.p2p_failed_sub'),
        );
      }
      const since = p2pWaitSince ?? Date.now();
      if (Date.now() - since > WEBRTC_TIMEOUT_MS) {
        showP2pWaitOverlay(
          translate('tv.p2p_timeout'),
          translate('tv.p2p_timeout_sub'),
        );
      }
      flushPendingRemoteStartIfReady();
    }, 400);
  }

  function shouldDeferRemoteStartForP2p(): boolean {
    return TV_REMOTE_GAME_REQUIRES_P2P && webrtcManager.getActiveCount() === 0;
  }

  webrtcManager.onIceCandidate = (playerId, candidate) => {
    const info = summarizeOutgoingIceCandidate(candidate);
    console.log(
      '[WebRTC][TV] send ICE via signaling',
      `room=${currentRoomCode || '-'}`,
      `player=${playerId || '-'}`,
      `type=${info.candidateType ?? '-'}`,
      `proto=${info.protocol ?? '-'}`,
      `mid=${info.sdpMid ?? '-'}`,
    );
    if (currentRoomCode) socket.sendWebRTCIce(currentRoomCode, playerId, candidate);
  };
  
  webrtcManager.onPeerStateChange = (playerId, state) => {
    if (state === 'failed') webrtcHasFailed = true;
    setCatchPoseOverlayConnection(webrtcManager.getActiveCount() > 0);
    if (state === 'connected') {
      webrtcHasFailed = false;
      webrtcRetryAttempted = false;
      if (playerConnectedAt !== null && !timeToConnectedMs.has(playerId)) {
        timeToConnectedMs.set(playerId, Date.now() - playerConnectedAt);
      }
      // `connected` приходит дважды (connectionstatechange + DataChannel open), поэтому
      // отправленный статус не сбрасываем: дедуп по статусу и так пропустит DIRECT после FAILED.
      startIceVerdictWindow();
      flushPendingRemoteStartIfReady();
      syncRemoteSessionToRoomSelection();
    }
    syncLobbyPostQrWaitVisibility();
    updateLobbyHints();
  };

  webrtcManager.onSelectedPairChange = () => {
    startIceVerdictWindow();
  };

  socket.onNetworkCheck((p) => {
    ipCheckResult = p.result;
    lanCheckResult = p.lan ?? null;
    // DIFFERENT_NETWORK — промежуточный: показываем, только пока нет вердикта по ICE.
    const hasIceVerdict =
      tvConnectionStatus !== null && tvConnectionStatus !== 'CONNECTING' && tvConnectionStatus !== 'DIFFERENT_NETWORK';
    if (p.result === 'different' && !hasIceVerdict && connectedPlayers.size > 0) {
      setTvConnectionStatus('DIFFERENT_NETWORK');
    }
    // Результат мог смениться после IPv4-отчёта уже после вердикта — пересчитать SAME_NETWORK_BLOCKED.
    if (hasIceVerdict) requestIceVerdict();
  });

  webrtcManager.onRemoteStream = (_playerId, stream) => {
    catchPoseStream = stream;
    setCatchPoseOverlayStream(stream);
    setCatchPoseOverlayConnection(stream !== null || webrtcManager.getActiveCount() > 0);
  };
  
  socket.onWebRTCOffer(async (payload) => {
    const playerId = payload.playerId ?? '';
    const sdp = payload.sdp;
    try {
      // A new offer means negotiation is alive; reset stale failure/timeout state.
      webrtcHasFailed = false;
      p2pWaitSince = Date.now();
      const answer = await webrtcManager.handleOffer(playerId, sdp);
      socket.sendWebRTCAnswer(payload.roomCode, playerId, answer);
      syncLobbyPostQrWaitVisibility();
      updateLobbyHints();
    } catch (err) {
      console.error('[WebRTC] failed to handle offer', err);
      webrtcHasFailed = true;
    }
  });
  
  socket.onWebRTCIce((payload) => {
    if (payload.playerId) webrtcManager.handleIceCandidate(payload.playerId, payload.candidate);
  });
  
  /**
   * Выбор в комнате (`room:*`, `tv:joined`) — истина: поднимаем калибровку/партии так,
   * чтобы `activeGameId` и фазы движков совпадали с `roomSelectedGameId` + режим.
   * Иначе P2P продолжает кормить fruit-remote, а target-attack в `lobby` — без скелета.
   */
  function syncRemoteSessionToRoomSelection(): void {
    if (connectedPlayers.size === 0) return;
    if (pendingRemoteStart) return;

    const roomGame = roomSelectedGameId;
    const roomMode = roomSelectedMode;
    const active = activeGameId;

    if (
      roomGame === 'target-attack' &&
      roomMode == null &&
      active === 'fruit-ninja-without-score'
    ) {
      stopGameAndShowLobby();
      return;
    }
    if (
      roomGame === 'fruit-ninja-without-score' &&
      roomMode == null &&
      active === 'target-attack'
    ) {
      stopGameAndShowLobby();
      return;
    }
    if (
      roomGame === 'leg-kick' &&
      roomMode == null &&
      active === 'fruit-ninja-without-score'
    ) {
      stopGameAndShowLobby();
      return;
    }
    if (
      roomGame === 'fruit-ninja-without-score' &&
      roomMode == null &&
      active === 'leg-kick'
    ) {
      stopGameAndShowLobby();
      return;
    }
    if (
      roomGame === 'target-attack' &&
      roomMode == null &&
      active === 'leg-kick'
    ) {
      stopGameAndShowLobby();
      return;
    }
    if (
      roomGame === 'leg-kick' &&
      roomMode == null &&
      active === 'target-attack'
    ) {
      stopGameAndShowLobby();
      return;
    }

    const runnerReady = roomGame === 'runner' && runnerStagePicked;
    const birdFlightReady = roomGame === 'bird-flight' || roomGame === 'chef-master' || roomGame === 'sky-race' || roomGame === 'laser-dodge' || roomGame === 'iq-web';
    if (!roomGame || !isRemoteGame(roomGame) || (roomMode == null && !runnerReady && !birdFlightReady)) return;

    // START уже поднял TA в playing, а tv:joined ещё прислал старый fruit — не сбрасывать партию.
    if (
      active === 'target-attack' &&
      targetAttackEngine.getPhase() === 'playing' &&
      roomGame === 'fruit-ninja-without-score'
    ) {
      return;
    }

    const selectedPhase =
      roomGame === 'target-attack'
        ? targetAttackEngine.getPhase()
        : roomGame === 'leg-kick'
          ? legKickEngine.getPhase()
          : roomGame === 'catch-pose'
            ? catchPosePhase
            : roomGame === 'runner'
              ? runnerEngine.getPhase()
              : roomGame === 'iq'
                ? iqEngine.getPhase()
                : roomGame === 'bird-flight'
                  ? birdFlightEngine.getPhase()
                  : roomGame === 'chef-master'
                    ? chefMasterEngine.getPhase()
                  : roomGame === 'sky-race'
                    ? skyRaceEngine.getPhase()
                  : roomGame === 'laser-dodge'
                    ? laserDodgeEngine.getPhase()
                  : roomGame === 'iq-web'
                    ? iqWebEngine.getPhase()
                  : roomGame === 'fruit-slice-web'
                    ? fruitSliceWebEngine.getPhase()
                    : remoteEngine.getPhase();
    const needRestart =
      active !== roomGame || (active === roomGame && selectedPhase === 'lobby');

    if (!needRestart) return;

    if (active !== null && isRemoteGame(active)) {
      stopGameAndShowLobby();
    }
    beginGameFromServer(roomGame);
  }

  function dispatchNativeGameFrame(playerId: string, frame: NativeGameFrame): void {
    syncRemoteSessionToRoomSelection();

    const taFrame = isTargetAttackNativeFrame(frame);
    const taRoom = roomSelectedGameId === 'target-attack';
    const taActive = activeGameId === 'target-attack';
    const lkRoom = roomSelectedGameId === 'leg-kick';
    const lkActive = activeGameId === 'leg-kick';
    const fruitActive = activeGameId === 'fruit-ninja-without-score';

    /**
     * Target Attack: WebRTC/Socket — один парсер. Кормим TA-движок, если кадр похож на TA и
     * (игра target-attack уже выбрана на ТВ **или** активная сессия target-attack), и не сценарий
     * «в комнате только fruit, активен fruit».
     */
    const feedTargetAttack =
      taFrame &&
      (taActive || taRoom) &&
      !(fruitActive && !taRoom) &&
      !lkActive && !lkRoom;

    if (feedTargetAttack) {
      targetAttackEngine.onGameFrame(playerId, frame);
    }

    /** Leg Kick: same parser as target-attack — route when leg-kick is selected/active. */
    const feedLegKick =
      taFrame &&
      (lkActive || lkRoom) &&
      !(fruitActive && !lkRoom);

    if (feedLegKick) {
      legKickEngine.onGameFrame(playerId, frame);
    }

    const feedFruit =
      activeGameId === 'fruit-ninja-without-score' && (!taFrame || (!taRoom && !lkRoom));

    if (feedFruit) {
      const phase = remoteEngine.getPhase();
      if (phase === 'playing' || phase === 'calibration') {
        remoteEngine.onGameFrame(playerId, frame);
      }
    }

    if (activeGameId === 'runner' && frame.gameState === 'PREPARING') {
      runnerEngine.onCalibFrame(frame);
    }
  }

  // Ring-buffer: keep only the latest frame per player; flushed once per rAF tick.
  const pendingFrames = new Map<string, NativeGameFrame>();

  function enqueueNativeFrame(playerId: string, frame: NativeGameFrame): void {
    pendingFrames.set(playerId, frame);
  }

  function flushPendingFrames(): void {
    if (pendingFrames.size === 0) return;
    pendingFrames.forEach((frame, playerId) => dispatchNativeGameFrame(playerId, frame));
    pendingFrames.clear();
  }

  webrtcManager.onNativeFrame = (playerId, frame) => {
    if (activeGameId === 'iq') {
      // KeypointsDataMultiplayer → calibration skeleton (parsed NativeGameFrame path)
      iqEngine.onCalibFrame(playerId, frame);
      return;
    }
    enqueueNativeFrame(playerId, frame);
  };
  webrtcManager.onUnknownFrame = (playerId, raw) => {
    // Headless: поза идёт в общий стор (меню, курсор, игра читают его сами), не в движок игры.
    if (isBodyPoseFrame(raw)) {
      const pose = parseBodyPoseFrame(raw);
      if (pose) bodyPose.push(pose, performance.now());
      return;
    }
    if (activeGameId === 'fruit-slice-web') {
      fruitSliceWebEngine.onGameFrame(raw);
      return;
    }
    if (activeGameId === 'runner') runnerEngine.onGameFrame(raw);
    if (activeGameId === 'bird-flight') birdFlightEngine.onGameFrame(raw);
    if (activeGameId === 'chef-master') chefMasterEngine.onGameFrame(raw);
    if (activeGameId === 'sky-race') skyRaceEngine.onGameFrame(raw);
    if (activeGameId === 'laser-dodge') laserDodgeEngine.onGameFrame(raw);
    if (activeGameId === 'iq-web') iqWebEngine.onGameFrame(raw);
    // IqTouchAnswerData arrives here (isIqFrame path); KeypointsDataMultiplayer is handled via onNativeFrame
    if (activeGameId === 'iq') iqEngine.onGameFrame(playerId, raw);
  };

  /**
   * Relay from server (Socket.IO `game:frame`) for Kotlin `GameMessage.*` payloads.
   * WebRTC DataChannel uses the same JSON; if both paths are enabled on the phone, frames may duplicate
   * — when P2P is up for that `playerId`, Socket frames are skipped.
   */
  socket.onGameFrame((payload) => {
    if (webrtcManager.isPeerConnected(payload.playerId)) return;
    // Поза headless-телефона ходит только по DataChannel (сервер game:frame с телефона не ретранслирует).
    if (isBodyPoseFrame(payload.rawFrame)) return;
    if (activeGameId === 'fruit-slice-web') {
      fruitSliceWebEngine.onGameFrame(payload.rawFrame);
      return;
    }
    // Runner and IQ send fields not in NativeGameFrame — route raw.
    if (activeGameId === 'runner') {
      runnerEngine.onGameFrame(payload.rawFrame);
      return;
    }
    if (activeGameId === 'bird-flight') {
      birdFlightEngine.onGameFrame(payload.rawFrame);
      return;
    }
    if (activeGameId === 'chef-master') {
      chefMasterEngine.onGameFrame(payload.rawFrame);
      return;
    }
    if (activeGameId === 'sky-race') {
      skyRaceEngine.onGameFrame(payload.rawFrame);
      return;
    }
    if (activeGameId === 'laser-dodge') {
      laserDodgeEngine.onGameFrame(payload.rawFrame);
      return;
    }
    if (activeGameId === 'iq-web') {
      iqWebEngine.onGameFrame(payload.rawFrame);
      return;
    }
    if (activeGameId === 'iq') {
      const iqParsed = parseNativeGameFrame(payload.rawFrame);
      if (iqParsed) {
        iqEngine.onCalibFrame(payload.playerId, iqParsed);
      } else {
        iqEngine.onGameFrame(payload.playerId, payload.rawFrame);
      }
      return;
    }
    const frame = parseNativeGameFrame(payload.rawFrame);
    if (!frame) return;
    enqueueNativeFrame(payload.playerId, frame);
  });

  /** @deprecated имя оставлено для вызовов из обработчиков — делегирует в sync */
  function tryAutoCalibrate(): void {
    syncRemoteSessionToRoomSelection();
  }
  
  // ── API-driven game start ─────────────────────────────────────────────────────
  socket.onRoomGameSelected((payload) => {
    cancelPendingRemoteStart();
    const prevGame = roomSelectedGameId;
    roomSelectedGameId = payload.gameId;
    roomSelectedGameSupported = payload.supported !== false;
    if (prevGame !== payload.gameId) {
      roomSelectedMode = null;
      roomSelectedPlayerCount = null;
    }
    if (!roomSelectedGameSupported) {
      if (gameUnsupportedOverlayEl) gameUnsupportedOverlayEl.hidden = false;
      if (lobbyChooseOverlayEl) lobbyChooseOverlayEl.hidden = true;
      if (activeGameId !== null) stopGameAndShowLobby();
      return;
    }
    if (gameUnsupportedOverlayEl) gameUnsupportedOverlayEl.hidden = true;
    if (activeGameId !== null) {
      // New game selected while a game is running — go back to lobby selection screen.
      stopGameAndShowLobby();
    } else {
      remoteEngine.setPhase('lobby');
      targetAttackEngine.setPhase('lobby');
      legKickEngine.setPhase('lobby');
      updateLobbyHints();
    }
    tryAutoCalibrate();
  });
  
  socket.onRoomStageSelected((payload) => {
    patchRunnerUi({ selectedStageId: payload.stageId, stageSelectVisible: false });
    runnerEngine.setStage(payload.stageId, payload.environment === '3d' ? '3d' : 'video');
    runnerStagePicked = true;
    updateLobbyHints();
    tryAutoCalibrate();
  });

  socket.onRoomModeSelected((payload) => {
    cancelPendingRemoteStart();
    if (payload.gameId) {
      roomSelectedGameId = payload.gameId;
      roomSelectedGameSupported = true;
      if (gameUnsupportedOverlayEl) gameUnsupportedOverlayEl.hidden = true;
    }
    roomSelectedMode = payload.mode;
    roomSelectedPlayerCount = payload.playerCount;
    targetAttackEngine.setPlayerCount(payload.playerCount);
    legKickEngine.setPlayerCount(payload.playerCount);
    remoteEngine.setPlayerCount(payload.playerCount);
    if (activeGameId !== null) {
      stopGameAndShowLobby();
    } else {
      updateLobbyHints();
    }
    tryAutoCalibrate();
  });
  
  socket.onRoomGameStart((payload) => {
    if (isRemoteGame(payload.gameId)) {
      const gid = payload.gameId as RemoteAuthoritativeId;
      if (shouldDeferRemoteStartForP2p()) {
        scheduleRemoteStartUntilP2p({ kind: 'phoneStart', gameId: gid });
        return;
      }
      executeRemotePlayingFromPhone(gid);
    } else {
      cancelPendingRemoteStart();
      console.warn('[TV] room:game-start: unsupported gameId', payload.gameId);
    }
  });

  socket.onRoomModeCleared((payload) => {
    cancelPendingRemoteStart();
    roomSelectedMode = null;
    roomSelectedPlayerCount = null;
    roomSelectedGameId = payload.selectedGameId ?? null;
    targetAttackEngine.setPlayerCount(1);
    remoteEngine.setPlayerCount(1);
    if (roomSelectedGameId === 'runner') {
      runnerStagePicked = false;
      patchRunnerUi({ stageSelectVisible: true });
    }
    if (activeGameId !== null) {
      stopGameAndShowLobby();
    } else {
      remoteEngine.setPhase('lobby');
      targetAttackEngine.setPhase('lobby');
      legKickEngine.setPhase('lobby');
      updateLobbyHints();
      tryAutoCalibrate();
    }
  });

  socket.onRoomGameCleared(() => {
    cancelPendingRemoteStart();
    roomSelectedGameId = null;
    roomSelectedMode = null;
    runnerStagePicked = false;
    roomSelectedPlayerCount = null;
    roomSelectedGameSupported = true;
    if (gameUnsupportedOverlayEl) gameUnsupportedOverlayEl.hidden = true;
    targetAttackEngine.setPlayerCount(1);
    remoteEngine.setPlayerCount(1);
    if (activeGameId !== null) {
      stopGameAndShowLobby();
    } else {
      remoteEngine.setPhase('lobby');
      targetAttackEngine.setPhase('lobby');
      legKickEngine.setPhase('lobby');
      updateLobbyHints();
    }
  });
  
  socket.onLocaleChanged((p) => {
    opts.onLocaleChange?.(p.locale);
  });

  // ── Lobby: create room + QR ───────────────────────────────────────────────────
  socket.onTvJoined((p) => {
      roomSelectedGameId = p.selectedGameId ?? null;
      roomSelectedMode = p.selectedMode ?? null;
      roomSelectedPlayerCount = p.selectedPlayerCount ?? null;
      roomSelectedGameSupported = p.selectedGameSupported !== false;
      targetAttackEngine.setPlayerCount(p.selectedPlayerCount ?? 1);
      legKickEngine.setPlayerCount(p.selectedPlayerCount ?? 1);
      remoteEngine.setPlayerCount(p.selectedPlayerCount ?? 1);
      if (gameUnsupportedOverlayEl) {
        gameUnsupportedOverlayEl.hidden = roomSelectedGameId === null || roomSelectedGameSupported;
      }
      updateLobbyHints();
      syncRemoteSessionToRoomSelection();
      if (p.ipv4ReportUrl) reportIpv4(p.ipv4ReportUrl);
    });
  
  async function initLobby(retried = false): Promise<void> {
    let code = tvRoomApi.current ? lockedRoomCode : null;
    if (!code) {
      const room = await tvRoomApi.createRoom();
      code = room.code;
      lockedRoomCode = code;
    }
  
    const chars = code.padEnd(4, '-').slice(0, 4).split('');
    roomCodeTiles.forEach((tile, i) => { tile.textContent = chars[i] ?? '-'; });
  
    let qr: string;
    try {
      qr = await tvRoomApi.qr(code, buildLanQueryFromTvPage());
    } catch (err) {
      /** Сессия ТВ истекла или комнаты нет (перезапуск сервера) — один раз создаём новую комнату. */
      if (err instanceof TvRoomGoneError && !retried) {
        tvRoomApi.reset();
        lockedRoomCode = null;
        return initLobby(true);
      }
      throw err;
    }
    qrImg.src = qr;
  
    currentRoomCode = code;
    socket.joinRoom(code);
    updateLobbyQrAndCodeVisibility();
  }
  
  /**
   * Останавливает игровые движки и показывает лобби без сброса игроков и комнаты.
   * Используется когда сервер присылает новый выбор игры/режима в процессе партии.
   */
  function stopGameAndShowLobby(): void {
    activeGameId = null;
    // Не трогаем roomSelected* — они отражают API/сокет; иначе onRoomModeSelected
    // сбрасывает только что присланный режим и tryAutoCalibrate не запускает target-attack.
    webrtcHasFailed = false;
    cancelPendingRemoteStart();
    remoteEngine.setPhase('lobby');
    targetAttackEngine.setPhase('lobby');
    legKickEngine.setPhase('lobby');
    catchPosePhase = 'lobby';
    iqEngine.stopGame();
    resetFruitNinjaRemoteUi();
    hideTargetAttackUi();
    hideLegKickUi();
    hideIqUi();
    setCatchPoseOverlayVisible(false);
    runnerEngine.reset();
    birdFlightEngine.reset();
    fruitSliceWebEngine.reset();
    chefMasterEngine.reset();
    headlessSkyRace.stop();
    skyRaceEngine.reset();
    laserDodgeEngine.reset();
    iqWebEngine.reset();
    // Keep catchPoseStream + overlay stream cached: peer is still alive, and
    // re-attaching the same MediaStream into a torn-down <video> requires us
    // to push it back through attachStream() on re-entry.
    setCatchPoseOverlayConnection(webrtcManager.getActiveCount() > 0);
    countdownLayer.style.display = 'none';
    countdownLayer.classList.remove('countdown--active');
    countdownGifEl.classList.remove('countdown-gif--visible');
    lobbyEl.style.display = 'flex';
    syncLobbyPostQrWaitVisibility();
    renderPlayersStrip();
    updateLobbyHints();
  }

  /** Забывает headless-телефон: комната закрыта или он ушёл (`player:disconnected` при закрытии комнаты не приходит). */
  function resetHeadlessState(): void {
    headlessPlayerId = null;
    headlessSkeleton.setEnabled(false);
    bodyPose.reset();
    headlessSkyRace.stop();
    headlessMenu.hide();
  }

  /** Возврат на экран подключения когда все игроки отключились во время игры. */
  function returnToLobby(): void {
    hideConnectionLostOverlay();
    // Close the current room on the server so players get tv:disconnected and
    // the old room code is invalidated. Always create a fresh room after.
    const codeToClose = currentRoomCode;
    lockedRoomCode = null;
    if (codeToClose) {
      void tvRoomApi.closeRoom(codeToClose);
    }

    activeGameId = null;
    roomSelectedGameId = null;
    roomSelectedMode = null;
    roomSelectedPlayerCount = null;
    runnerStagePicked = false;
    remoteEngine.setPlayerCount(1);
    playerConnectedAt = null;
    webrtcHasFailed = false;
    cancelPendingRemoteStart();
    connectedPlayers.clear();
    resetHeadlessState();
    resetConnectionStatus();
    remoteEngine.setPhase('lobby');
    targetAttackEngine.setPhase('lobby');
    legKickEngine.setPhase('lobby');
    catchPosePhase = 'lobby';
    resetFruitNinjaRemoteUi();
    hideTargetAttackUi();
    hideLegKickUi();
    setCatchPoseOverlayVisible(false);
    runnerEngine.reset();
    birdFlightEngine.reset();
    fruitSliceWebEngine.reset();
    chefMasterEngine.reset();
    headlessSkyRace.stop();
    skyRaceEngine.reset();
    laserDodgeEngine.reset();
    iqWebEngine.reset();
    setCalibrationAlert(null);
    setCatchPoseOverlayConnection(false);
    catchPoseStream = null;
    setCatchPoseOverlayStream(null);
    countdownLayer.style.display = 'none';
    countdownLayer.classList.remove('countdown--active');
    countdownGifEl.classList.remove('countdown-gif--visible');
    lobbyEl.style.display = 'flex';
    hideConnectingOverlay();
    syncLobbyPostQrWaitVisibility();
    renderPlayersStrip();
    updateLobbyHints();
    void initLobby();
  }

  function forceDisconnectAndReturnToQr(): void {
    // During transient socket reconnect we keep current room/code to avoid churn.
    if (!signalingConnected) return;
    if (resettingAfterP2pLoss) return;
    resettingAfterP2pLoss = true;
    // On hard ICE RTT/P2P failure we must rotate room code:
    // next initLobby() creates a fresh room, and server closes the old one.
    lockedRoomCode = null;
    webrtcManager.closeAll();
    connectedPlayers.clear();
    resetHeadlessState();
    showConnectionLostOverlay();
    window.setTimeout(() => {
      resettingAfterP2pLoss = false;
    }, 1500);
  }
  
  // ── Game flow ─────────────────────────────────────────────────────────────────
  function beginGameFromServer(gameId: string): void {
    patchFruitNinjaRemoteUi({ gameOverVisible: false });
    patchTargetAttackUi({ gameOverVisible: false });

    // IQ: no calibration phase — start immediately.
    if (gameId === 'iq') {
      const rid = gameId as RemoteAuthoritativeId;
      if (shouldDeferRemoteStartForP2p()) {
        scheduleRemoteStartUntilP2p({ kind: 'calibrate', gameId: rid });
        return;
      }
      executeRemoteCalibrationFromServer(rid);
      return;
    }

    // Remote games: calibration first; game starts after slice / native start.
    if (
      gameId === 'fruit-ninja-without-score' ||
      gameId === 'target-attack' ||
      gameId === 'catch-pose' ||
      gameId === 'leg-kick' ||
      gameId === 'runner' ||
      gameId === 'bird-flight' ||
      gameId === 'chef-master' ||
      gameId === 'sky-race' ||
      gameId === 'laser-dodge' ||
      gameId === 'iq-web' ||
      gameId === 'fruit-slice-web'
    ) {
      const rid = gameId as RemoteAuthoritativeId;
      if (shouldDeferRemoteStartForP2p()) {
        scheduleRemoteStartUntilP2p({ kind: 'calibrate', gameId: rid });
        return;
      }
      executeRemoteCalibrationFromServer(rid);
      return;
    }

    console.warn('[TV] beginGameFromServer: unsupported gameId', gameId);
  }
  
  function restartGame(): void {
    resetFruitNinjaRemoteUi();
    hideTargetAttackUi();
    if (activeGameId) {
      beginGameFromServer(activeGameId);
    }
  }

  window.restartGame = restartGame;

  /** ICE RTT (ms) last sampled in `updateWebRtcRttDisplay` — для строки `#webrtc-stats`. */
  let lastIceRttMs: number | null = null;
  let lastTransportKind: 'TURN' | 'STUN' | 'MIXED' | null = null;
  const hwThreads = typeof navigator.hardwareConcurrency === 'number' ? navigator.hardwareConcurrency : null;
  const devMemGb =
    typeof (navigator as Navigator & { deviceMemory?: number }).deviceMemory === 'number'
      ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? null
      : null;

  type MemoryPerf = Performance & {
    memory?: {
      usedJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
  };
  function readJsHeapStats(): string {
    const mem = (performance as MemoryPerf).memory;
    if (!mem || mem.jsHeapSizeLimit <= 0) return 'RAM(js) —';
    const usedMb = Math.round(mem.usedJSHeapSize / (1024 * 1024));
    const limitMb = Math.round(mem.jsHeapSizeLimit / (1024 * 1024));
    return `RAM(js) ${usedMb}/${limitMb}MB`;
  }
  let rafFramesForFps = 0;
  let fpsWindowStart = performance.now();
  
  // ── Game loop ─────────────────────────────────────────────────────────────────
  let rafId: number | null = null;
  type LoopBranch = 'catch-pose' | 'fruit' | 'leg-kick' | 'target-attack' | 'runner' | 'iq' | 'bird-flight' | 'chef-master' | 'sky-race' | 'laser-dodge' | 'iq-web' | 'fruit-slice-web' | 'idle';
  let lastLoopBranch: LoopBranch | null = null;
  function applyLoopBranchTransition(branch: LoopBranch): void {
    if (lastLoopBranch === branch) return;
    lastLoopBranch = branch;
    if (branch === 'catch-pose') {
      hideFruitRemoteHudAndPause();
      patchFruitNinjaRemoteUi({ gameOverVisible: false });
      hideTargetAttackUi();
      hideLegKickUi();
      setCatchPoseOverlayVisible(true);
    } else if (branch === 'fruit') {
      setCatchPoseOverlayVisible(false);
      hideTargetAttackUi();
      hideLegKickUi();
    } else if (branch === 'leg-kick') {
      setCatchPoseOverlayVisible(false);
      hideFruitRemoteHudAndPause();
      patchFruitNinjaRemoteUi({ gameOverVisible: false });
      hideTargetAttackUi();
    } else if (branch === 'target-attack') {
      setCatchPoseOverlayVisible(false);
      hideFruitRemoteHudAndPause();
      patchFruitNinjaRemoteUi({ gameOverVisible: false });
      hideLegKickUi();
      hideIqUi();
    } else if (branch === 'iq') {
      setCatchPoseOverlayVisible(false);
      hideFruitRemoteHudAndPause();
      patchFruitNinjaRemoteUi({ gameOverVisible: false });
      hideLegKickUi();
      hideTargetAttackUi();
    } else {
      setCatchPoseOverlayVisible(false);
      hideFruitRemoteHudAndPause();
      patchFruitNinjaRemoteUi({ gameOverVisible: false });
      hideLegKickUi();
      hideTargetAttackUi();
      hideIqUi();
    }
  }
  let lastCatchPoseDiagLogAt = 0;
  function maybeLogCatchPoseMediaDiagnostics(): void {
    const now = performance.now();
    if (now - lastCatchPoseDiagLogAt < 3000) return;
    lastCatchPoseDiagLogAt = now;
    void (async () => {
      try {
        const [diag, transport, rtt] = await Promise.all([
          webrtcManager.getMediaDiagnostics(),
          webrtcManager.getTransportKind(),
          webrtcManager.getAverageRoundTripTimeMs(),
        ]);
        console.log(
          '[CatchPose] media',
          `transport=${transport ?? '-'}`,
          `rtt=${rtt !== null ? `${Math.round(rtt)}ms` : '-'}`,
          `diag=${diag ? JSON.stringify(diag) : '-'}`,
        );
      } catch (err) {
        console.warn('[CatchPose] diagnostics failed', err);
      }
    })();
  }

  /** Логотип скрыт только в калибровке / обратном отсчёте / игре (включая паузу); виден в лобби, оверлеях и game over. */
  function isGameplayOnScreen(): boolean {
    if (activeGameId === null) return false;
    const phase =
      activeGameId === 'target-attack'
        ? targetAttackEngine.getPhase()
        : activeGameId === 'leg-kick'
          ? legKickEngine.getPhase()
          : activeGameId === 'catch-pose'
            ? catchPosePhase
            : activeGameId === 'bird-flight'
              ? birdFlightEngine.getPhase()
              : activeGameId === 'chef-master'
                ? chefMasterEngine.getPhase()
              : activeGameId === 'sky-race'
                ? skyRaceEngine.getPhase()
              : activeGameId === 'laser-dodge'
                ? laserDodgeEngine.getPhase()
              : activeGameId === 'iq-web'
                ? iqWebEngine.getPhase()
              : activeGameId === 'fruit-slice-web'
                ? fruitSliceWebEngine.getPhase()
                : activeGameId === 'runner'
                  ? runnerEngine.getPhase()
                  : activeGameId === 'iq'
                    ? iqEngine.getPhase()
                    : remoteEngine.getPhase();
    return phase !== 'lobby' && phase !== 'gameover';
  }

  function loop(): void {
    rafFramesForFps += 1;
    const hideLogo = isGameplayOnScreen();
    if (tvLogoEl.hidden !== hideLogo) tvLogoEl.hidden = hideLogo;
    flushPendingFrames();
    const taPhase = targetAttackEngine.getPhase();
    const lkPhase = legKickEngine.getPhase();
    const tickTargetAttack =
      activeGameId === 'target-attack' ||
      (roomSelectedGameId === 'target-attack' && taPhase !== 'lobby' && taPhase !== 'countdown');
    const tickLegKick =
      activeGameId === 'leg-kick' ||
      (roomSelectedGameId === 'leg-kick' && lkPhase !== 'lobby' && lkPhase !== 'countdown');

    if (activeGameId === 'iq') {
      applyLoopBranchTransition('iq');
      iqEngine.tick();
      syncIqUi();
    } else if (activeGameId === 'runner') {
      applyLoopBranchTransition('runner');
      runnerEngine.tick();
    } else if (activeGameId === 'bird-flight') {
      // Свой цикл Three.js и своя канва — общую 2D только очищаем под ней.
      applyLoopBranchTransition('bird-flight');
      renderer.clearCanvas();
    } else if (activeGameId === 'fruit-slice-web') {
      // Своя канва поверх общей — общую только очищаем.
      applyLoopBranchTransition('fruit-slice-web');
      renderer.clearCanvas();
    } else if (activeGameId === 'chef-master') {
      // Игра — в iframe поверх общей канвы; её только очищаем.
      applyLoopBranchTransition('chef-master');
      renderer.clearCanvas();
    } else if (activeGameId === 'sky-race') {
      // Игра — в iframe поверх общей канвы; её только очищаем.
      applyLoopBranchTransition('sky-race');
      renderer.clearCanvas();
    } else if (activeGameId === 'laser-dodge') {
      // Игра — на своей канве поверх общей; общую только очищаем.
      applyLoopBranchTransition('laser-dodge');
      renderer.clearCanvas();
    } else if (activeGameId === 'iq-web') {
      // Игра — в iframe поверх общей канвы; её только очищаем.
      applyLoopBranchTransition('iq-web');
      renderer.clearCanvas();
    } else if (activeGameId === 'catch-pose' && catchPosePhase === 'playing') {
      applyLoopBranchTransition('catch-pose');
      // Video pixels are composited directly by the browser from the visible
      // <video> element — no canvas draw needed.
      setCatchPoseOverlayConnection(catchPoseStream !== null || webrtcManager.getActiveCount() > 0);
      maybeLogCatchPoseMediaDiagnostics();
    } else if (activeGameId === 'fruit-ninja-without-score' && roomSelectedGameId !== 'target-attack' && roomSelectedGameId !== 'leg-kick') {
      applyLoopBranchTransition('fruit');
      remoteEngine.tick();
      syncFruitRemoteUi();
    } else if (tickLegKick) {
      applyLoopBranchTransition('leg-kick');
      legKickEngine.tick();
      syncLegKickUi();
    } else {
      if (tickTargetAttack) {
        applyLoopBranchTransition('target-attack');
        targetAttackEngine.tick();
        syncTargetAttackUi();
      } else {
        applyLoopBranchTransition('idle');
        renderer.clearCanvas();
      }
    }
    rafId = requestAnimationFrame(loop);
  }
  
  // ── WebRTC RTT overlay (ICE getStats, ~ping) ─────────────────────────────────
  function setWebrtcRttClasses(kind: 'idle' | 'wait' | 'ok' | 'mid' | 'high'): void {
    webrtcRttEl.classList.remove(
      'webrtc-rtt--idle',
      'webrtc-rtt--ok',
      'webrtc-rtt--mid',
      'webrtc-rtt--high',
    );
    if (kind === 'idle') webrtcRttEl.classList.add('webrtc-rtt--idle');
    if (kind === 'wait') {
      /* default neutral styling */
    } else if (kind === 'ok') webrtcRttEl.classList.add('webrtc-rtt--ok');
    else if (kind === 'mid') webrtcRttEl.classList.add('webrtc-rtt--mid');
    else if (kind === 'high') webrtcRttEl.classList.add('webrtc-rtt--high');
  }
  
  async function updateWebRtcRttDisplay(): Promise<void> {
    lastIceRttMs = null;
    lastTransportKind = null;
    if (!signalingConnected) {
      webrtcRttEl.textContent = 'P2P: reconnect signaling…';
      setWebrtcRttClasses('wait');
      // Повторяется каждый тик (1 с), пока сигналинг лежит, — таймер сбрасывается;
      // после реконнекта тост сам скроется через 5 с (в лобби без игрока вердикта, который бы его заменил, нет).
      showNetToast(translate('tv.net.unstable'), 5_000);
      // После восстановления сигналинга вердикт покажется заново.
      tvConnectionStatus = null;
      return;
    }
    if (webrtcManager.getPeerCount() === 0) {
      const hasPlayer = connectedPlayers.size > 0;
      const timedOut =
        playerConnectedAt !== null && Date.now() - playerConnectedAt > WEBRTC_TIMEOUT_MS;
  
      if (hasPlayer && (webrtcHasFailed || timedOut)) {
        if (timedOut || webrtcHasFailed) {
          // Нет connected за 15 с (или ICE failed) → FAILED на ТВ и телефоне; ретрай идёт дальше.
          connectedPlayers.forEach((_name, id) => sendConnectionStatus(id, 'FAILED', null));
          setTvConnectionStatus('FAILED');
        }
        if (!webrtcRetryAttempted) {
          // First failure: retry WebRTC once (fresh peer/ICE cycle) before disconnecting room.
          webrtcRetryAttempted = true;
          webrtcHasFailed = false;
          playerConnectedAt = Date.now();
          webrtcManager.closeAll();
          webrtcRttEl.textContent = translate('tv.p2p_reconnecting');
          setWebrtcRttClasses('wait');
          return;
        }
        webrtcRttEl.textContent = translate('tv.p2p_no_link');
        setWebrtcRttClasses('high');
        if (activeGameId === null) {
          forceDisconnectAndReturnToQr();
        }
        return;
      }
      if (hasPlayer) {
        webrtcRttEl.textContent = 'P2P …';
        setWebrtcRttClasses('wait');
        return;
      }
      webrtcRttEl.textContent = 'P2P —';
      setWebrtcRttClasses('idle');
      return;
    }
  
    const active = webrtcManager.getActiveCount();
    if (active === 0) {
      webrtcRttEl.textContent = 'P2P …';
      setWebrtcRttClasses('wait');
      return;
    }
  
    const rtt = await webrtcManager.getAverageRoundTripTimeMs();
    lastTransportKind = await webrtcManager.getTransportKind();
    if (rtt === null) {
      webrtcRttEl.textContent = 'P2P …';
      setWebrtcRttClasses('wait');
      return;
    }
  
    const ms = Math.round(rtt);
    lastIceRttMs = ms;
    webrtcRttEl.textContent = `P2P ${ms} ms`;
    if (ms < 55) setWebrtcRttClasses('ok');
    else if (ms < 140) setWebrtcRttClasses('mid');
    else setWebrtcRttClasses('high');

  }

  const statsIntervalId = window.setInterval(() => {
    const dcPerSec = webrtcManager.takeDataChannelMessagesSinceLastSample();
    const parseErrPerSec = webrtcManager.takeParseErrorsSinceLastSample();
    const frameAgeMs = webrtcManager.getLastParsedFrameAgeMs();
    const now = performance.now();
    const elapsedSec = (now - fpsWindowStart) / 1000;
    fpsWindowStart = now;
    const fps =
      elapsedSec > 0 ? Math.min(999, Math.round(rafFramesForFps / elapsedSec)) : 0;
    rafFramesForFps = 0;

    void (async () => {
      await updateWebRtcRttDisplay();
      tickIceVerdictWindow();
      if (webrtcStatsEl) {
        const ping = lastIceRttMs !== null ? `${lastIceRttMs} ms` : '—';
        const route =
          lastTransportKind === null
            ? '—'
            : lastTransportKind === 'MIXED'
              ? 'TURN/STUN'
              : lastTransportKind;
        const heap = readJsHeapStats();
        const cores = hwThreads !== null ? `Cores ${hwThreads}` : 'Cores —';
        const devMem = devMemGb !== null ? `DevMem ${devMemGb}GB` : 'DevMem —';
        const frameAge = frameAgeMs !== null ? `${Math.min(99999, Math.round(frameAgeMs))}ms` : '—';
        const media = await webrtcManager.getMediaDiagnostics();
        const vBytes = media?.videoBytesReceived ?? 0;
        const vPkts = media?.videoPacketsReceived ?? 0;
        const vFrames = media?.videoFramesDecoded ?? 0;
        const vFps = media?.videoFramesPerSecond;
        const vCodec = media?.videoCodec ?? '—';
        const conn = media?.connectionState ?? '—';
        const ice = media?.iceConnectionState ?? '—';
        const vBytesKb = Math.round(vBytes / 1024);
        const vFpsStr = typeof vFps === 'number' ? Math.round(vFps).toString() : '—';
        const jbAvg = media?.jitterBufferAvgMs;
        const jbTgt = media?.jitterBufferTargetMs;
        const jbAvgStr = typeof jbAvg === 'number' ? `${Math.round(jbAvg)}ms` : '—';
        const jbTgtStr = typeof jbTgt === 'number' ? `${Math.round(jbTgt)}ms` : '—';
        const netJitter = media?.jitterMs;
        const netJitterStr = typeof netJitter === 'number' ? `${Math.round(netJitter)}ms` : '—';
        const fDropped = media?.framesDropped ?? 0;
        const fReceived = media?.framesReceived ?? 0;
        const pktLost = media?.packetsLost ?? 0;
        const fw = media?.frameWidth ?? null;
        const fh = media?.frameHeight ?? null;
        const res = fw !== null && fh !== null ? `${fw}x${fh}` : '—';
        const line1 =
          `FPS ${fps} · DC ${dcPerSec}/s · ICE RTT ${ping} · ${route} · ` +
          `Vid ${vBytesKb}KB/${vPkts}pkt/${vFrames}fr@${vFpsStr}fps ${vCodec} ${res} · ` +
          `conn=${conn} ice=${ice} · FrameAge ${frameAge} · ParseErr ${parseErrPerSec}/s`;
        const line2 =
          `JBavg ${jbAvgStr} · JBtgt ${jbTgtStr} · NetJit ${netJitterStr} · ` +
          `Drop ${fDropped}/${fReceived} · Lost ${pktLost} · ${heap} · ${cores} · ${devMem} · ` +
          `pair=${lastIcePair ?? '—'} ipcheck=${ipCheckResult ?? '—'} lan=${lanCheckResult ?? '—'}`;
        webrtcStatsEl.textContent = `${line1}\n${line2}`;
      }
    })();
  }, 1000);
  
  // ── Init ─────────────────────────────────────────────────────────────────────
  resizeCanvas();
  setCatchPoseOverlayVisible(false);
  setCatchPoseOverlayConnection(false);
  setCatchPoseOverlayStream(null);
  void initLobby();
  syncLobbyPostQrWaitVisibility();
  const onLobbyPostQrRefreshClick = (): void => {
    window.location.reload();
  };
  const onTvSendLogsClick = (): void => {
    void sendTvClientLogs();
  };
  const onNetToastDismissClick = (): void => {
    dismissConnectionBanner();
  };
  /** OK / Back на пульте закрывают баннер статуса (Tizen Back = 10009, webOS Back = 461). */
  const onNetToastKeydown = (e: KeyboardEvent): void => {
    if (!netToastDismissible) return;
    const isOk = e.key === 'Enter';
    const isBack =
      e.key === 'Escape' || e.key === 'Backspace' || e.key === 'GoBack' || e.key === 'BrowserBack' ||
      e.keyCode === 10009 || e.keyCode === 461;
    if (!isOk && !isBack) return;
    e.preventDefault();
    dismissConnectionBanner();
  };
  netToastDismissBtnEl?.addEventListener('click', onNetToastDismissClick);
  window.addEventListener('keydown', onNetToastKeydown);
  if (lobbyPostQrRefreshBtnEl) {
    lobbyPostQrRefreshBtnEl.addEventListener('click', onLobbyPostQrRefreshClick);
  }
  if (lobbyQrRefreshBtnEl) {
    lobbyQrRefreshBtnEl.addEventListener('click', onLobbyPostQrRefreshClick);
  }
  if (tvSendLogsBtnEl) {
    tvSendLogsBtnEl.addEventListener('click', onTvSendLogsClick);
  }
  loop();

  return () => {
    window.removeEventListener('resize', resizeCanvas);
    releaseWakeLock();
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    clearP2pWaitPoll();
    clearLobbyPostQrRefreshTimer();
    if (lobbyPostQrRefreshBtnEl) {
      lobbyPostQrRefreshBtnEl.removeEventListener('click', onLobbyPostQrRefreshClick);
    }
    if (lobbyQrRefreshBtnEl) {
      lobbyQrRefreshBtnEl.removeEventListener('click', onLobbyPostQrRefreshClick);
    }
    if (tvSendLogsBtnEl) {
      tvSendLogsBtnEl.removeEventListener('click', onTvSendLogsClick);
    }
    netToastDismissBtnEl?.removeEventListener('click', onNetToastDismissClick);
    window.removeEventListener('keydown', onNetToastKeydown);
    headlessSkyRace.dispose();
    headlessMenu.dispose();
    headlessSkeleton.dispose();
    window.clearInterval(statsIntervalId);
    window.removeEventListener('error', onWindowError);
    window.removeEventListener('unhandledrejection', onWindowUnhandledRejection);
    console.log = nativeConsole.log;
    console.info = nativeConsole.info;
    console.warn = nativeConsole.warn;
    console.error = nativeConsole.error;
    console.debug = nativeConsole.debug;
    setCatchPoseOverlayVisible(false);
    setCatchPoseOverlayConnection(false);
    setCatchPoseOverlayStream(null);
    webrtcManager.closeAll();
    socket.dispose();
    fruitSoundEngine.dispose();
    targetAttackSoundEngine.dispose();
    legKickSoundEngine.dispose();
    window.restartGame = () => undefined;
  };
}

declare global {
  interface Window {
    restartGame: () => void;
  }
}
